"use client";

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  Suspense,
  type ChangeEvent,
} from "react";
import { useSearchParams, useRouter } from "next/navigation";
import {
  RefreshCw,
  ImagePlus,
  Leaf,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Check,
  Clock,
  Sparkles,
  Share2,
  Truck,
  AlertTriangle,
  Info,
  Crop,
  Wand2,
} from "lucide-react";

// Force dynamic rendering due to useSearchParams
export const dynamic = "force-dynamic";
import { Header } from "@/components/shared/Header";
import { Footer } from "@/components/shared/Footer";
import { TenantProvider } from "@/lib/tenant/context";
import { getFeaturesForTenant } from "@/lib/tenant/features";
import { effectivePlantQuantity } from "@/lib/quantity";
import {
  PhotoUploader,
  type UploadMeta,
} from "@/components/design/PhotoUploader";
import { AreaSelector } from "@/components/design/AreaSelector";
import { ZipcodeEntry } from "@/components/design/ZipcodeEntry";
import {
  RetailerSelector,
  Retailer,
} from "@/components/design/RetailerSelector";
import { StylePicker, Style } from "@/components/design/StylePicker";
import {
  PlantList,
  Plant,
  RecipeInfo,
  MAX_SELECTED_PLANTS,
} from "@/components/design/PlantList";
import { PlantDetailsModal } from "@/components/design/PlantDetailsModal";
import {
  ResultViewer,
  SeasonEditHistories,
} from "@/components/design/ResultViewer";
import {
  EditVersion,
  PlantReconciliationResult,
} from "@/components/design/EditPanel";
import { EmailModal } from "@/components/design/EmailModal";
import { DesignCanvas } from "@/components/design/DesignCanvas";
import { MakeItHappen } from "@/components/design/MakeItHappen";
import { PlantingGuideCard } from "@/components/design/PlantingGuideCard";
import { ShoppingList } from "@/components/design/ShoppingList";
import {
  GenerationLoader,
  recordGenerationDuration,
} from "@/components/design/GenerationLoader";
import { NatureLoader } from "@/components/shared/NatureLoader";
import { AlertModal } from "@/components/shared/AlertModal";
import { HorticulturalDisclaimer } from "@/components/shared/HorticulturalDisclaimer";
import { useAlert } from "@/hooks/useAlert";
import { CartModal } from "@/components/design/CartModal";
import { StockWarningModal } from "@/components/design/StockWarningModal";
import {
  PlantAlternativesDialog,
  type AlternativesChoice,
} from "@/components/design/PlantAlternativesDialog";
import type {
  DesignPlant,
  RecipeAlternativesResponse,
} from "@/lib/substitution/alternatives-api";
import { isRecipePulled } from "@/lib/commerce/recipe-pull";
import { useCart } from "@/lib/cart/CartContext";
import type { CartItem } from "@/lib/cart/types";
import { toPermanentUrl } from "@/lib/utils";
import {
  capture,
  setRetailerGroup,
  getDistinctId,
  registerSuperProps,
} from "@/lib/analytics/client";
import { markDesignCompleted } from "@/lib/analytics/person";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { StepValueNugget } from "@/components/design/StepValueNugget";
import { GardenTypeToggle } from "@/components/container/GardenTypeToggle";
import { GardenTypeChooser } from "@/components/container/GardenTypeChooser";
import { hasContainerGarden } from "@/lib/container/access";
import { PhotoCropper, type CropRect } from "@/components/design/PhotoCropper";
import {
  D2C_STOREFRONT_TENANT_ID,
  PLANTISTA_GC_RETAILER_ID,
} from "@/lib/commerce/constants";
import {
  getShipRestriction,
  plantistaRedirectUrl,
  type ShipRestriction,
} from "@/lib/tenant/shipping";
import { useRecipeAvailability } from "@/lib/cart/useRecipeAvailability";
import {
  computeMaskCoverage,
  MIN_MASK_COVERAGE,
  SMALL_MASK_COVERAGE,
  FIX_MASK_TARGET_COVERAGE,
  growPolygonsToCoverage,
} from "@/lib/maskCoverage";
import {
  ProductRecommendation,
  groupRecommendationsByProduct,
} from "@/lib/productRecommendations";
import { useServiceStatus } from "@/hooks/useServiceStatus";
import { ServiceUnavailableBanner } from "@/components/shared/ServiceUnavailableBanner";
import {
  DesignerOutagePage,
  buildOutageStore,
} from "@/components/shared/DesignerOutagePage";

// Automatic photo check (PLA2-822): below this share of yard in the frame, the
// area step suggests cropping. Starting point from the mockup; tune with the
// photo_checked yard_fraction data.
const MIN_YARD_FRACTION = 0.3;

// Outline-help chips (PLA2-822): what the user is outlining → how to draw it.
type OutlineKind = "foundation" | "driveway" | "tree" | "yard";
const OUTLINE_TIPS: { kind: OutlineKind; label: string; tip: string }[] = [
  {
    kind: "foundation",
    label: "Foundation bed",
    tip: "Start at the wall and go out to where the bed should end, usually 3 to 6 feet. Not a thin line along the siding.",
  },
  {
    kind: "driveway",
    label: "Driveway edge",
    tip: "Include the whole strip of soil from the pavement edge to the lawn. Leave the pavement itself out.",
  },
  {
    kind: "tree",
    label: "Around a tree",
    tip: "Circle the whole bed out to the edge of the branches, not just the trunk.",
  },
  {
    kind: "yard",
    label: "Whole yard",
    tip: "Outline the lawn and beds right up to the edges you want planted. Leave out the house, paving and street.",
  },
];

type Step =
  | "upload"
  | "area"
  | "preferences"
  | "retailer-select"
  | "handoff"
  | "shipRestricted"
  | "style"
  | "plants"
  | "generate"
  | "result";

/**
 * Selection under the MAX_SELECTED_PLANTS cap where substitutes the shopper
 * explicitly picked win over auto-selected recipe extras (a 14-plant recipe
 * with 3 sold out would otherwise fill all 12 slots before any swap).
 */
function withSubstitutesFirst(
  plantIds: string[],
  substituteIds: string[]
): string[] {
  const subs = [...new Set(substituteIds)].slice(0, MAX_SELECTED_PLANTS);
  const subSet = new Set(subs);
  const rest = plantIds
    .filter((id) => !subSet.has(id))
    .slice(0, MAX_SELECTED_PLANTS - subs.length);
  return [...rest, ...subs];
}

/** Substitute card (API shape, nulls) → the page's Plant shape (optionals). */
function designPlantToPlant(p: DesignPlant): Plant {
  return {
    id: p.id,
    commonName: p.commonName,
    scientificName: p.scientificName,
    imageUrl: p.imageUrl ?? undefined,
    sunLevel: p.sunLevel,
    category: p.category,
    description: p.description || undefined,
    waterNeeds: p.waterNeeds ?? undefined,
    zones: p.zones,
    careInstructions: p.careInstructions ?? undefined,
    height: p.height ?? undefined,
    width: p.width ?? undefined,
    bloomTime: p.bloomTime ?? undefined,
    bloomColor: p.bloomColor ?? undefined,
    price: p.price,
    shopifyVariantId: p.shopifyVariantId,
  };
}

// Map internal steps to display steps for progress indicator
const getProgressStep = (step: Step): Step => {
  if (
    step === "retailer-select" ||
    step === "handoff" ||
    step === "shipRestricted" ||
    step === "style"
  )
    return "preferences";
  return step;
};

// Re-key backend plant counts (keyed by plant NAME) to the frontend's plant IDs.
// Mirrors Pro's mapCountsByPlantId — tolerates alias vs commonName vs genusName
// so counts for variety names like "Ruby Muhly Grass (Undaunted)" still land.
function mapCountsByPlantId(
  rawCounts: Record<string, number> | undefined | null,
  plants: Array<Record<string, any>>
): Record<string, number> {
  if (!rawCounts) return {};
  const byId: Record<string, number> = {};
  for (const p of plants) {
    const id: string | undefined = p.id || p.genusAliasId;
    if (!id) continue;
    const candidates = [
      p.alias,
      p.commonName,
      p.common_name,
      p.genusName,
    ].filter(Boolean) as string[];
    let count: number | undefined;
    for (const name of candidates) {
      if (typeof rawCounts[name] === "number") {
        count = rawCounts[name];
        break;
      }
      const lower = name.toLowerCase();
      for (const [k, v] of Object.entries(rawCounts)) {
        if (k.toLowerCase() === lower) {
          count = v as number;
          break;
        }
      }
      if (count !== undefined) break;
    }
    // Keep an explicit 0 (counter said "not planted"): it's meaningful and must
    // survive so the UI can render "Not used". Only a missing count stays absent.
    if (typeof count === "number" && count >= 0) byId[id] = count;
  }
  return byId;
}

// PLA2-548/551: resolve a plant-count-map species NAME (as returned by the
// backend's plant identification pass) back to the frontend's plant id.
// Same alias/commonName/genusName tolerance as mapCountsByPlantId above.
function findPlantIdByName(
  name: string,
  plants: Array<Record<string, any>>
): string | undefined {
  const lower = name.toLowerCase();
  for (const p of plants) {
    const id: string | undefined = p.id || p.genusAliasId;
    if (!id) continue;
    const candidates = [
      p.alias,
      p.commonName,
      p.common_name,
      p.genusName,
    ].filter(Boolean) as string[];
    if (candidates.some((c) => c.toLowerCase() === lower)) return id;
  }
  return undefined;
}

export function DesignPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const cart = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  // After-gen "Send to Email" stock-warning popup (PLA2-344).
  const [isStockWarningOpen, setIsStockWarningOpen] = useState(false);
  const [isSendingDesignEmail, setIsSendingDesignEmail] = useState(false);
  const {
    isOpen: isAlertOpen,
    config: alertConfig,
    showConfirm,
    showError,
    showSuccess,
    showInfo,
    closeAlert,
  } = useAlert();
  const [step, setStep] = useState<Step>("upload");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [backendImageUrl, setBackendImageUrl] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  // Share of the photo the drawn outline covers (0–1), or null while unknown.
  // Tiny outlines leave the model no room to plant, so Continue gates on it
  // (PLA2-822).
  const [maskCoverage, setMaskCoverage] = useState<number | null>(null);
  const [isCroppingUploaded, setIsCroppingUploaded] = useState(false);
  // Automatic photo check (PLA2-822): how much of the uploaded photo is yard,
  // plus a suggested crop. Keyed by the backend URL it was run on.
  const [photoCheck, setPhotoCheck] = useState<{
    url: string;
    yardFraction: number;
    cropBox: CropRect | null;
  } | null>(null);
  const [photoCheckDismissed, setPhotoCheckDismissed] = useState(false);
  // "Fix it for me": pushes grown polygons into the AreaSelector.
  const [polygonOverride, setPolygonOverride] = useState<{
    data: import("@/components/design/AreaSelector").PolygonData;
    version: number;
  } | null>(null);
  const [outlineAutoGrown, setOutlineAutoGrown] = useState(false);
  const [outlineKind, setOutlineKind] = useState<OutlineKind | null>(null);
  const [polygonData, setPolygonData] = useState<
    import("@/components/design/AreaSelector").PolygonData | null
  >(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  // Shown when a customer tries to pick more than MAX_SELECTED_PLANTS (PLA2-762).
  const [plantLimitNotice, setPlantLimitNotice] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null
  );
  const [projectId, setProjectId] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [copyingLink, setCopyingLink] = useState(false);
  // Explicit marketing opt-in captured with the email (PLA2-373). Unchecked by
  // default; persisted alongside customerEmail and sent with the design email.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // PLA2-945: backend dependency checklist. While a blocking service is down
  // the page shows one banner and the upload / generate actions are disabled.
  const {
    unavailable: serviceUnavailable,
    checking: serviceChecking,
    recheck: recheckServices,
  } = useServiceStatus();
  const [recipes, setRecipes] = useState<Style[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<Step>>(new Set());
  const [retailerData, setRetailerData] = useState<any>(null);
  const [loadingRetailer, setLoadingRetailer] = useState(false); // Start false - no loading until retailer selected
  const [retailerSelected, setRetailerSelected] = useState(
    !!searchParams.get("retailerId")
  ); // Track if a retailer has been selected
  const [userZipcode, setUserZipcode] = useState<string | null>(null);
  const [userZone, setUserZone] = useState<string | null>(null);
  const [nearbyRetailers, setNearbyRetailers] = useState<Retailer[]>([]);
  const [availablePlants, setAvailablePlants] = useState<Plant[]>([]);
  const [loadingPlants, setLoadingPlants] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeInfo | null>(null);
  const [selectedPlantForModal, setSelectedPlantForModal] =
    useState<Plant | null>(null);
  const [generationResults, setGenerationResults] = useState<{
    // SUMMER is the always-generated baseline/hero image. spring (like autumn
    // and winter) is only present in all-seasons mode, so it is optional.
    summer: string;
    spring?: string;
    autumn: string;
    winter: string;
    gif: string;
    diagramUrl?: string;
  } | null>(null);
  const [editHistories, setEditHistories] = useState<SeasonEditHistories>({});
  const [editedResults, setEditedResults] = useState<Record<string, string>>(
    {}
  );
  const [plantCounts, setPlantCounts] = useState<Record<string, number>>({});
  const [generateAllSeasons, setGenerateAllSeasons] = useState(true);
  // Plant counting always runs — it drives the shopping list quantities.
  const countPlants = true;
  // PLA2-318: partner to hand off to when a D2C ZIP falls in their territory.
  const [handoffPartner, setHandoffPartner] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // PLA2-688: the retailer can't ship to the entered ZIP's state.
  const [shipRestriction, setShipRestriction] =
    useState<ShipRestriction | null>(null);
  const [plantSubStep, setPlantSubStep] = useState<"plants" | "gen-options">(
    "plants"
  );

  // Reset plant sub-step when entering plants step
  useEffect(() => {
    if (step === "plants") {
      setPlantSubStep("plants");
    }
  }, [step]);

  // Async generation state
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<
    | "queued"
    | "processing"
    | "rate_limited"
    | "retrying"
    | "completed"
    | "failed"
  >("queued");
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [queuePosition, setQueuePosition] = useState<number | undefined>(
    undefined
  );
  const [estimatedWait, setEstimatedWait] = useState<number | undefined>(
    undefined
  );

  const progressSteps: Array<{
    key: Step;
    label: string;
    description: string;
  }> = [
    {
      key: "upload",
      label: "Upload Photo",
      description: "Add your yard photo",
    },
    {
      key: "area",
      label: "Select Area",
      description: "Define the target design zone",
    },
    {
      key: "preferences",
      label: "Input Preferences",
      description: "Location and style",
    },
    {
      key: "plants",
      label: "Refine Plants",
      description: "Customize your selection",
    },
    {
      key: "result",
      label: "Receive Design",
      description: "Review the AI design",
    },
  ];

  // The default storefront UUID — Plantista's own garden center (seed DB record is
  // still named "Alex Garden Center"). Used for any consumer not locked to a partner.
  const DEFAULT_RETAILER_ID = "00000000-0000-0000-0000-000000000000";

  // Get retailer ID from URL params or use default, but allow state to override
  // `?retailerId=<D2C storefront tenant>` is a D2C entry, not a retailer: that id
  // is a Shopify commerce tenant with no retailer row, so every retailer lookup
  // (branding, recipes, plants) would 404. Look those up on the Plantista
  // garden-center retailer instead; `entryD2C` below still marks the flow D2C,
  // and `commerceTenantId` still sells from the storefront.
  const rawRetailerParam = searchParams.get("retailerId");
  const urlRetailerId =
    rawRetailerParam === D2C_STOREFRONT_TENANT_ID
      ? PLANTISTA_GC_RETAILER_ID
      : rawRetailerParam || DEFAULT_RETAILER_ID;
  const isRetailerLocked = !!searchParams.get("retailerId");
  const [selectedRetailerId, setSelectedRetailerId] =
    useState<string>(urlRetailerId);

  // Use the selected retailer ID (from state or URL)
  const retailerId = selectedRetailerId;

  // The Plantista-owned, direct-to-consumer flow (PLA2-340). It drives Plantista
  // (not retailer) branding, per-step value-nugget copy, the after-gen purchase
  // fork (PLA2-344), and Shopify commerce. Two things turn it on:
  //   • `entryD2C` — an explicit `/start` → `/design?d2c=1` entry (persisted so a
  //     resumed/cross-device session stays D2C).
  //   • the **default store** — any consumer ZIP not served by a partner retailer
  //     falls back to the Plantista storefront, which IS the D2C store. So a plain
  //     `/design` visit and the out-of-area ZIP fallback are D2C by default; only a
  //     retailer locked via `?retailerId=` is a non-D2C (retailer-branded) flow.
  const [entryD2C, setEntryD2C] = useState<boolean>(
    () =>
      searchParams.get("d2c") === "1" ||
      searchParams.get("retailerId") === D2C_STOREFRONT_TENANT_ID
  );
  const isDefaultStore = retailerId === DEFAULT_RETAILER_ID;
  const isD2C = entryD2C || isDefaultStore;

  // Container gardens: a garden center with the CONTAINER_GARDEN add-on gets an
  // Outdoor / Container toggle above the photo upload. Outdoor is this
  // page; container is its own flow at /design/container. D2C reads the add-on
  // off the Plantista garden-center retailer, the catalog it designs from.
  const [hasContainerAddon, setHasContainerAddon] = useState(false);
  useEffect(() => {
    const catalogId = isD2C ? PLANTISTA_GC_RETAILER_ID : retailerId;
    let live = true;
    fetch(`/api/retailer/${catalogId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (data) =>
          live && setHasContainerAddon(hasContainerGarden(data?.feature_access))
      )
      .catch(() => live && setHasContainerAddon(false));
    return () => {
      live = false;
    };
  }, [isD2C, retailerId]);

  // PLA2-966 step 1, "What are we planting?": shown before the photo upload
  // to add-on retailers only, once per tab session. Skipped when the shopper
  // already chose outdoor (the container page's toggle sends `from=toggle`)
  // or is past the upload (a restored / reopened design).
  const GARDEN_CHOSEN_KEY = "plantista-garden-type-chosen";
  const [gardenChosen, setGardenChosen] = useState<boolean>(() => {
    if (searchParams.get("from") === "toggle") return true;
    try {
      return (
        typeof window !== "undefined" &&
        sessionStorage.getItem(GARDEN_CHOSEN_KEY) === "1"
      );
    } catch {
      return false;
    }
  });
  const rememberGardenChosen = (chosen: boolean) => {
    setGardenChosen(chosen);
    try {
      if (chosen) sessionStorage.setItem(GARDEN_CHOSEN_KEY, "1");
      else sessionStorage.removeItem(GARDEN_CHOSEN_KEY);
    } catch {
      /* storage blocked: the chooser just shows again next visit */
    }
  };

  // The store whose Shopify stock this flow sells from — also sent with the
  // generate request so the server can re-check stock (PLA2-687).
  const commerceTenantId = isD2C ? D2C_STOREFRONT_TENANT_ID : retailerId;

  // Plants the server's generation stock gate rejected (PLA2-687): stock can
  // drop after the availability read below, and the gate's fresh read wins.
  // Also fed into the recipe rollup so a recipe the gate pulled (PLA2-613)
  // reads as sold out in the style grid.
  const [gateSoldOutIds, setGateSoldOutIds] = useState<Set<string>>(
    () => new Set()
  );

  // Shopify-driven recipe/plant stock. Records are empty for non-Shopify
  // tenants or while loading, so the style grid renders unchanged until data
  // arrives (progressive enhancement — never blocks the grid).
  const {
    recipeAvailability,
    plantAvailability,
    selling: retailerSelling,
  } = useRecipeAvailability(recipes, commerceTenantId, gateSoldOutIds, {
    // Pull rule after swapping: the server counts sold-out plants that have
    // an in-stock substitute from this retailer's catalog, in this zone.
    catalogRetailerId: retailerId,
    zone: userZone,
  });

  // ── Out-of-stock alternatives ─────────────────────────────────────────────
  // originalPlantId → the in-stock substitute the shopper picked, for the
  // selected recipe. Substitutes join availablePlants + the selection like any
  // recipe plant, and ride to the generation gate so it applies the recipe
  // pull rule after swapping.
  const [plantSubstitutions, setPlantSubstitutions] = useState<
    Record<string, Plant>
  >({});
  const substituteIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    substituteIdsRef.current = new Set(
      Object.values(plantSubstitutions).map((p) => p.id)
    );
  }, [plantSubstitutions]);
  const [alternativesDialog, setAlternativesDialog] = useState<{
    recipeId: string;
    recipeName: string;
    trigger: "select" | "gate";
    status: "loading" | "ready" | "error";
    result: RecipeAlternativesResponse | null;
    /** Gate retry: the selection the gate rejected (may include substitutes). */
    plantIds?: string[];
  } | null>(null);

  // Ecommerce-enabled flow: D2C, or a garden center whose own Shopify store is
  // live (commerce_connection enabled). These share the D2C generation options
  // (plant counting always on — the count drives purchase quantities) and the
  // "Make it Happen" purchase/email fork on the result page.
  const isEcommerce = isD2C || retailerSelling;

  // Does this retailer actually have a live coupon to include in the design
  // email? (PLA2-10 — email CTA copy only promises a coupon when one exists.)
  // Fossil Creek runs its own event/coupon flow, handled separately, so it
  // never counts here. Mirrors the same purpose/deleted filter used when
  // building the email payload's `coupons` array.
  const FOSSIL_CREEK_ID = "e955b2c5-d47d-4ae4-971f-36ed0deb87f2";
  const retailerHasCoupon =
    retailerId !== FOSSIL_CREEK_ID &&
    !!retailerData?.coupons?.some(
      (c: any) =>
        !c.deleted && ["DESIGN", "PLANTING", "FERTILIZING"].includes(c.purpose)
    );

  // ── Out-of-stock exclusion (PLA2-458) ──────────────────────────────────────
  // plantAvailability only carries plants with a real Shopify stock signal
  // (unmapped plants are absent — a catalog gap must never read as sold out),
  // so `false` here means Shopify explicitly reported no stock. Plus the
  // gate-rejected plants (gateSoldOutIds, above).
  const soldOutPlantIds = useMemo<Set<string>>(() => {
    const soldOut = new Set<string>(gateSoldOutIds);
    for (const [plantId, available] of Object.entries(plantAvailability)) {
      if (available === false) soldOut.add(plantId);
    }
    return soldOut;
  }, [plantAvailability, gateSoldOutIds]);

  // Read inside effects that must not re-run when availability changes.
  const soldOutPlantIdsRef = useRef(soldOutPlantIds);
  useEffect(() => {
    soldOutPlantIdsRef.current = soldOutPlantIds;
  }, [soldOutPlantIds]);

  // Array form for child props (memoized so children don't see a new array
  // reference on every render).
  const soldOutPlantIdsList = useMemo(
    () => Array.from(soldOutPlantIds),
    [soldOutPlantIds]
  );

  // Plants dropped from the selection because they went out of stock — shown as
  // an "unavailable" note on the shopping list.
  const unavailablePlants = useMemo(
    () => availablePlants.filter((p) => soldOutPlantIds.has(p.id)),
    [availablePlants, soldOutPlantIds]
  );

  // Availability lands asynchronously, so anything already selected that turns
  // out to be sold out gets dropped here. Returning `prev` unchanged bails the
  // update.
  useEffect(() => {
    if (soldOutPlantIds.size === 0) return;
    setSelectedPlants((prev) => {
      const next = prev.filter((id) => !soldOutPlantIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [soldOutPlantIds]);

  // Read cached branding from sessionStorage to avoid logo flicker on navigation.
  // Initialize to null on both server and client to prevent hydration mismatch,
  // then populate from sessionStorage in an effect after mount.
  const [cachedBranding, setCachedBranding] = useState<any>(null);
  useEffect(() => {
    try {
      const id = searchParams.get("retailerId");
      if (!id) return;
      const cached = sessionStorage.getItem(`plantista-branding-${id}`);
      if (cached) setCachedBranding(JSON.parse(cached));
    } catch {}
  }, [searchParams]);

  // Session storage key
  const SESSION_KEY = "plantista-design-session";
  const [sessionRestored, setSessionRestored] = useState(false);

  // Load state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem(SESSION_KEY);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        console.log(
          "[Design Page] Restoring session state, isRetailerLocked:",
          isRetailerLocked
        );

        // Restore state
        // Don't restore into the transient handoff step (PLA2-318) — the partner
        // isn't persisted, so fall back to the ZIP step instead of a blank screen.
        if (parsed.step)
          setStep(
            parsed.step === "handoff" || parsed.step === "shipRestricted"
              ? "preferences"
              : parsed.step
          );
        // Use backendImageUrl for display if uploadedImageUrl is not available
        if (parsed.backendImageUrl) {
          setBackendImageUrl(parsed.backendImageUrl);
          setUploadedImageUrl(parsed.backendImageUrl); // Use backend URL for display
        }
        if (parsed.selectedArea) setSelectedArea(parsed.selectedArea);
        if (parsed.selectedStyle) setSelectedStyle(parsed.selectedStyle);
        if (parsed.selectedPlants)
          setSelectedPlants(
            parsed.selectedPlants.slice(0, MAX_SELECTED_PLANTS)
          );
        if (parsed.userZipcode) setUserZipcode(parsed.userZipcode);
        if (parsed.userZone) setUserZone(parsed.userZone);
        // Only restore retailer from session if NOT locked via URL param
        // URL param should always take precedence
        if (!isRetailerLocked) {
          if (parsed.selectedRetailerId)
            setSelectedRetailerId(
              parsed.selectedRetailerId === D2C_STOREFRONT_TENANT_ID
                ? PLANTISTA_GC_RETAILER_ID
                : parsed.selectedRetailerId
            );
          if (parsed.retailerSelected !== undefined)
            setRetailerSelected(parsed.retailerSelected);
        } else {
          console.log(
            "[Design Page] Retailer locked via URL, using:",
            urlRetailerId
          );
        }
        if (parsed.completedSteps)
          setCompletedSteps(new Set(parsed.completedSteps));
        // Restore availablePlants so the result page survives a refresh
        // even if the recipe was removed from the retailer's list on the
        // backend (server would return 404 and we'd lose the data).
        if (Array.isArray(parsed.availablePlants))
          setAvailablePlants(parsed.availablePlants);
        if (
          parsed.plantSubstitutions &&
          typeof parsed.plantSubstitutions === "object"
        )
          setPlantSubstitutions(parsed.plantSubstitutions);
        if (parsed.selectedRecipe) setSelectedRecipe(parsed.selectedRecipe);
        if (parsed.generationResults)
          setGenerationResults(parsed.generationResults);
        if (parsed.editHistories) setEditHistories(parsed.editHistories);
        if (parsed.editedResults) setEditedResults(parsed.editedResults);
        if (parsed.projectId) setProjectId(parsed.projectId);
        if (parsed.customerEmail) setCustomerEmail(parsed.customerEmail);
        if (typeof parsed.marketingConsent === "boolean")
          setMarketingConsent(parsed.marketingConsent);
        if (parsed.generatedImageUrl)
          setGeneratedImageUrl(parsed.generatedImageUrl);
        if (parsed.plantCounts) setPlantCounts(parsed.plantCounts);
        // Keep the D2C flag sticky across refresh/resume so branding + nuggets
        // persist (the `?d2c=1` param may not survive a shared resume link).
        // But a retailer locked via ?retailerId= must win over a stale D2C
        // session from the same tab — restoring entryD2C there would force
        // Plantista branding on a retailer-branded link.
        if (
          parsed.entryD2C &&
          (!isRetailerLocked || rawRetailerParam === D2C_STOREFRONT_TENANT_ID)
        )
          setEntryD2C(true);
        // generateAllSeasons + countPlants intentionally NOT restored —
        // PLA2-261 wants them to default to true on every fresh load.
        // Restore async state
        if (parsed.jobId) setJobId(parsed.jobId);
        if (parsed.jobStatus) setJobStatus(parsed.jobStatus);
        if (parsed.jobProgress !== undefined)
          setJobProgress(parsed.jobProgress);
      } catch (error) {
        console.error("[Design Page] Failed to restore session:", error);
      }
    } else {
      console.log(
        "[Design Page] No session to restore. URL retailerId:",
        urlRetailerId,
        "isLocked:",
        isRetailerLocked
      );
    }
    setSessionRestored(true);
  }, []);

  // PLA2-819: the ZIP-step gate (PLA2-688) only fires on ZIP submit, so a
  // restored session or shared resume link could land a restricted-state user
  // on plant selection / results and let them pick or cart plants the retailer
  // can't ship. Enforce the same restriction on every step past the ZIP.
  useEffect(() => {
    if (!sessionRestored) return;
    if (
      step !== "style" &&
      step !== "plants" &&
      step !== "generate" &&
      step !== "result"
    )
      return;
    const restriction = getShipRestriction(selectedRetailerId, userZipcode);
    if (!restriction) return;
    capture(ANALYTICS_EVENTS.SHIP_RESTRICTED_SHOWN, {
      state: restriction.state,
      zipcode: userZipcode,
    });
    setShipRestriction(restriction);
    setStep("shipRestricted");
  }, [sessionRestored, step, selectedRetailerId, userZipcode]);

  // Log initial retailer configuration + fire design_started (PLA2-366).
  useEffect(() => {
    console.log(
      "[Design Page] Initial config - retailerId:",
      retailerId,
      "retailerSelected:",
      retailerSelected,
      "isRetailerLocked:",
      isRetailerLocked
    );
    // PLA2-973: every later event in the session says which flow it came
    // from; the container page registers "container" the same way.
    registerSuperProps({ garden_type: "outdoor" });
    capture(ANALYTICS_EVENTS.DESIGN_STARTED, {
      retailer_id: retailerId,
      account_type: isD2C ? "d2c" : "gc",
    });
  }, []);

  // Save state to sessionStorage whenever it changes (only after restore completes to avoid clobbering)
  useEffect(() => {
    if (!sessionRestored) return;
    const stateToSave = {
      step,
      // Don't store uploadedImageUrl (base64 data) - it's too large for sessionStorage
      // We'll use backendImageUrl instead to restore the image display
      backendImageUrl,
      selectedArea,
      selectedStyle,
      selectedPlants,
      userZipcode,
      userZone,
      selectedRetailerId,
      retailerSelected,
      completedSteps: Array.from(completedSteps),
      // Persist availablePlants so refresh on the result page still has the
      // full plant data for the shopping list, even if the recipe gets
      // deleted server-side (would otherwise 404 on refetch).
      availablePlants,
      plantSubstitutions,
      selectedRecipe,
      generationResults,
      editHistories,
      editedResults,
      projectId,
      customerEmail,
      marketingConsent,
      generatedImageUrl,
      plantCounts,
      entryD2C,
      // Async generation state
      jobId,
      jobStatus,
      jobProgress,
    };

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      // Handle QuotaExceededError gracefully
      if (
        error instanceof DOMException &&
        error.name === "QuotaExceededError"
      ) {
        console.warn(
          "[Design Page] SessionStorage quota exceeded, clearing old data"
        );
        // Clear session storage and try again with minimal data
        sessionStorage.removeItem(SESSION_KEY);
        try {
          // Save only critical data
          const minimalState = {
            step,
            backendImageUrl,
            selectedArea,
            selectedStyle,
            selectedPlants,
            projectId,
          };
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(minimalState));
        } catch (retryError) {
          console.error(
            "[Design Page] Failed to save even minimal state:",
            retryError
          );
        }
      } else {
        console.error("[Design Page] Failed to save session state:", error);
      }
    }
  }, [
    sessionRestored,
    step,
    backendImageUrl,
    selectedArea,
    selectedStyle,
    selectedPlants,
    userZipcode,
    userZone,
    selectedRetailerId,
    retailerSelected,
    completedSteps,
    availablePlants,
    plantSubstitutions,
    selectedRecipe,
    generationResults,
    editHistories,
    editedResults,
    projectId,
    customerEmail,
    marketingConsent,
    generatedImageUrl,
    plantCounts,
    entryD2C,
    jobId,
    jobStatus,
    jobProgress,
  ]);

  // Persist the full flow state to the Project row as the user advances (PLA2-315),
  // so mid-funnel abandonment leaves a complete, resumable DB row and the design can
  // be reopened cross-device via the capability link (PLA2-314). The Project is
  // created early (after area select), so this fires on EVERY step once projectId
  // exists — not just at result. Mirrors the sessionStorage blob exactly so the
  // resume page can seed it straight back. Best-effort + debounced so rapid changes
  // collapse into one PATCH.
  useEffect(() => {
    if (!sessionRestored || !projectId) return;

    const resumeState = {
      step,
      backendImageUrl,
      selectedArea,
      selectedStyle,
      selectedPlants,
      userZipcode,
      userZone,
      selectedRetailerId,
      retailerSelected,
      completedSteps: Array.from(completedSteps),
      availablePlants,
      plantSubstitutions,
      selectedRecipe,
      generationResults,
      editHistories,
      editedResults,
      projectId,
      customerEmail,
      generatedImageUrl,
      plantCounts,
      entryD2C,
    };

    const timer = setTimeout(() => {
      fetch(`/api/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeState }),
      }).catch((err) =>
        console.warn("[Design Page] Failed to persist resume state:", err)
      );
    }, 1200);

    return () => clearTimeout(timer);
  }, [
    sessionRestored,
    step,
    projectId,
    backendImageUrl,
    selectedArea,
    selectedStyle,
    selectedRetailerId,
    retailerSelected,
    completedSteps,
    plantSubstitutions,
    userZipcode,
    userZone,
    generationResults,
    editedResults,
    editHistories,
    plantCounts,
    customerEmail,
    selectedPlants,
    availablePlants,
    selectedRecipe,
    entryD2C,
  ]);

  // Scroll to shopping list when returning from cart page
  useEffect(() => {
    if (step === "result" && availablePlants.length > 0 && generationResults) {
      const shouldScroll = sessionStorage.getItem(
        "plantista-scroll-to-shopping"
      );
      if (shouldScroll) {
        sessionStorage.removeItem("plantista-scroll-to-shopping");
        // Wait for DOM to render the shopping list
        requestAnimationFrame(() => {
          document
            .getElementById("shopping-list")
            ?.scrollIntoView({ behavior: "smooth" });
        });
      }
    }
  }, [step, availablePlants, generationResults]);

  // Handle starting over - clear session and reset all state
  const handleStartOver = () => {
    showConfirm(
      "Are you sure you want to start over? This will clear all your progress.",
      () => {
        console.log("[Design Page] Starting over - clearing session");
        sessionStorage.removeItem(SESSION_KEY);

        // Reset all state
        setStep("upload");
        rememberGardenChosen(false);
        setUploadedFile(null);
        setUploadedImageUrl(null);
        setBackendImageUrl(null);
        setSelectedArea(null);
        setSelectedStyle(null);
        setSelectedPlants([]);
        setGeneratedImageUrl(null);
        setProjectId(null);
        setCustomerEmail(null);
        setCompletedSteps(new Set());
        setUserZipcode(null);
        setNearbyRetailers([]);
        setAvailablePlants([]);
        setSelectedRecipe(null);
        setGenerationResults(null);
        setUploadError(null);
        setPlantCounts({});

        // Keep retailer info if locked via URL
        if (!isRetailerLocked) {
          setSelectedRetailerId(DEFAULT_RETAILER_ID);
          setRetailerSelected(false);
        }
      },
      "Start Over"
    );
  };

  // State-clearing helpers for checkpoint navigation
  const clearStep1State = () => {
    setUserZipcode(null);
    setNearbyRetailers([]);
    if (!isRetailerLocked) {
      setSelectedRetailerId(DEFAULT_RETAILER_ID);
      setRetailerSelected(false);
    }
  };

  const clearStep2State = () => {
    setSelectedStyle(null);
    setSelectedRecipe(null);
  };

  const clearStep3State = () => {
    // Don't clear availablePlants or selectedPlants here — they are derived
    // from the selected recipe. If the recipe changes, the useEffect that
    // depends on selectedStyle will re-fetch plants and auto-select them.
    // Clearing them here would leave an empty list with no way to re-populate
    // when navigating back to style and then forward again with the same recipe.
  };

  const clearStep4State = () => {
    setGenerationResults(null);
    setGeneratedImageUrl(null);
    setProjectId(null);
    setCustomerEmail(null);
    setPlantCounts({});
  };

  const clearStepsAfter = (targetProgressIndex: number) => {
    const stepKeysByIndex: Step[][] = [
      ["upload", "area"],
      ["preferences"],
      ["style"],
      ["plants"],
      ["generate", "result"],
    ];
    const clearFns = [
      clearStep1State,
      clearStep2State,
      clearStep3State,
      clearStep4State,
    ];

    // Clear state and completed-step keys for every index after target
    for (let i = targetProgressIndex + 1; i < stepKeysByIndex.length; i++) {
      if (i - 1 < clearFns.length) clearFns[i - 1]();
      setCompletedSteps((prev) => {
        const next = new Set(prev);
        for (const key of stepKeysByIndex[i]) next.delete(key);
        return next;
      });
    }
  };

  // Fetch retailer branding from database - only after a retailer is selected (after step 3)
  useEffect(() => {
    // Don't fetch until a retailer has been selected
    if (!retailerSelected) {
      return;
    }

    const fetchRetailerBranding = async () => {
      setLoadingRetailer(true);
      try {
        console.log("[Design Page] Fetching retailer branding:", retailerId);
        const response = await fetch(`/api/retailer/${retailerId}`);
        if (response.ok) {
          const data = await response.json();
          console.log(
            "[Design Page] Retailer data loaded:",
            data.business_name
          );
          setRetailerData(data);
          // Cache branding for flicker-free future navigations
          try {
            const bc = data.branding_config || {};
            sessionStorage.setItem(
              `plantista-branding-${retailerId}`,
              JSON.stringify({
                // Never cache the demo name for the default storefront.
                companyName:
                  data.business_name === "Alex Garden Center"
                    ? "Plantista"
                    : data.business_name,
                logo: bc.logo_url || data.logo_url,
                primaryColor: bc.color_palette?.primary,
                secondaryColor: bc.color_palette?.secondary,
                tertiaryColor: bc.color_palette?.tertiary,
              })
            );
          } catch {}
        } else {
          console.error(
            "[Design Page] Failed to fetch retailer:",
            response.status
          );
        }
      } catch (error) {
        console.error("[Design Page] Error fetching retailer branding:", error);
      } finally {
        setLoadingRetailer(false);
      }
    };
    fetchRetailerBranding();
  }, [retailerId, retailerSelected]);

  // Extract branding from database, falling back to sessionStorage cache to avoid flicker.
  // In the D2C flow (which includes the default Plantista storefront) we ignore retailer
  // branding entirely and present Plantista's own brand — never "Alex Garden Center".
  const brandingConfig = retailerData?.branding_config || {};
  const branding = isD2C
    ? {
        companyName: "Plantista",
        logo: "/logo512.png",
        primaryColor: "#5cc990", // Plantista 400
        secondaryColor: "#ccff00", // Neon Green
        tertiaryColor: "#f0fdf4",
      }
    : {
        companyName:
          retailerData?.business_name &&
          retailerData.business_name !== "Alex Garden Center"
            ? retailerData.business_name
            : cachedBranding?.companyName || "Plantista AI Designer",
        logo:
          brandingConfig.logo_url ||
          retailerData?.logo_url ||
          cachedBranding?.logo ||
          "/logo512.png",
        primaryColor:
          brandingConfig.color_palette?.primary ||
          cachedBranding?.primaryColor ||
          "#10b981",
        secondaryColor:
          brandingConfig.color_palette?.secondary ||
          cachedBranding?.secondaryColor ||
          "#059669",
        tertiaryColor:
          brandingConfig.color_palette?.tertiary ||
          cachedBranding?.tertiaryColor ||
          "#f0fdf4",
      };

  const tenant = {
    type: (isD2C ? "consumer" : "garden_center") as
      | "consumer"
      | "garden_center",
    retailerId: retailerData?.id || DEFAULT_RETAILER_ID,
    branding,
    features: getFeaturesForTenant(isD2C ? "consumer" : "garden_center"),
  };

  // GC retailer Group so anonymous design events aggregate by retailer (PLA2-366).
  useEffect(() => {
    if (!retailerId) return;
    setRetailerGroup(retailerId, {
      name: branding.companyName,
      accountType: isD2C ? "d2c" : "gc",
    });
    // Once retailer state has actually settled, pin the resolved identity onto
    // every later event. session_started can only guess these from the URL.
    registerSuperProps({
      retailer_id: retailerId,
      retailer_name: branding.companyName,
      account_type: isD2C ? "d2c" : "gc",
    });
  }, [retailerId, branding.companyName, isD2C]);

  // Fetch recipes from retailer - only after retailer is selected
  useEffect(() => {
    // Don't fetch until a retailer has been selected
    if (!retailerSelected) {
      console.log(
        "[Design Page] Skipping recipe fetch - retailer not yet selected"
      );
      return;
    }

    const fetchRecipes = async () => {
      setLoadingRecipes(true);
      try {
        console.log(
          "[Design Page] Fetching recipes for retailer:",
          retailerId,
          "(locked:",
          isRetailerLocked,
          ")"
        );
        const params = `?retailerId=${retailerId}`;
        const response = await fetch(`/api/recipes${params}`);
        if (response.ok) {
          const data = await response.json();
          console.log(
            "[Design Page] Loaded",
            data.length,
            "recipes for retailer:",
            retailerId
          );
          if (data.length > 0) {
            console.log(
              "[Design Page] First recipe:",
              data[0].name,
              "| retailerId:",
              data[0].retailerId
            );
          }
          setRecipes(data);
        } else {
          console.error(
            "[Design Page] Failed to fetch recipes:",
            response.statusText
          );
          setRecipes([]);
        }
      } catch (error) {
        console.error("[Design Page] Error fetching recipes:", error);
        setRecipes([]);
      } finally {
        setLoadingRecipes(false);
      }
    };

    fetchRecipes();
  }, [retailerId, retailerSelected]);

  const markUploadComplete = () => {
    setCompletedSteps((prev) => new Set(prev).add("upload"));
  };

  const handleFileUpload = async (
    file: File,
    // Photo-rights + source metadata (PLA2-372 / Sys10). Defaults cover the
    // in-flow "replace photo" path, where rights were already attested on the
    // initial upload and the source is a user upload.
    meta: UploadMeta = { source: "upload", rightsConfirmed: true }
  ) => {
    if (serviceUnavailable) return;
    // Re-upload detection: if a photo already exists, clear all downstream state
    if (uploadedImageUrl || backendImageUrl) {
      clearStepsAfter(0);
      setSelectedArea(null);
    }

    setUploadedFile(file);
    setPhotoCheck(null);
    setPhotoCheckDismissed(false);
    photoCheckUrlRef.current = null;
    setOutlineAutoGrown(false);
    setIsUploading(true); // Start loader
    setUploadError(null);

    // Convert file to data URL (base64) for persistence across navigation
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setUploadedImageUrl(dataUrl); // Use data URL instead of blob URL for persistence
    };
    reader.readAsDataURL(file);

    // Compress image to stay under Vercel's 4.5MB body limit
    const MAX_UPLOAD_BYTES = 4 * 1024 * 1024; // 4MB — stay under Vercel's 4.5MB limit

    const compressImage = (
      f: File,
      maxDim = 2048,
      quality = 0.85
    ): Promise<File> =>
      new Promise((resolve) => {
        if (f.size <= MAX_UPLOAD_BYTES) {
          resolve(f);
          return;
        }
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas
            .getContext("2d")!
            .drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) =>
              resolve(
                new File([blob!], f.name.replace(/\.\w+$/, ".jpg"), {
                  type: "image/jpeg",
                })
              ),
            "image/jpeg",
            quality
          );
        };
        img.src = URL.createObjectURL(f);
      });

    // Upload via Next.js API proxy (avoids CORS issues with direct backend calls)
    try {
      const compressed = await compressImage(file);
      if (compressed.size > MAX_UPLOAD_BYTES) {
        const sizeMB = (compressed.size / 1024 / 1024).toFixed(1);
        throw new Error(
          `Image is too large (${sizeMB}MB) even after compression. Please use a smaller image (under 4MB).`
        );
      }
      const formData = new FormData();
      formData.append("file", compressed);
      // Photo-rights + moderation metadata (PLA2-372 / Sys10).
      formData.append("source", meta.source);
      formData.append("rightsConfirmed", String(meta.rightsConfirmed));

      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        // Surface the server's friendly reason (moderation block, rights gate,
        // etc.) instead of a generic message. 422 == rejected by moderation.
        const errData = await response.json().catch(() => null);
        const detail =
          errData?.detail ||
          (response.status === 422
            ? "This photo can't be used. Please try a different image."
            : "Failed to upload image");
        if (response.status === 422) {
          showError(detail, "Photo Not Accepted");
        }
        throw new Error(detail);
      }

      const result = await response.json();
      const backendUrl = result.url || result.imageUrl;
      if (result.moderation?.decision === "flag") {
        console.warn(
          "[Design Page] Upload flagged by moderation:",
          result.moderation.flags
        );
      }
      console.log("[Design Page] Image uploaded to backend:", backendUrl);

      // Store backend URL separately for generation API
      setBackendImageUrl(backendUrl);
      void runPhotoCheck(backendUrl);
      capture(ANALYTICS_EVENTS.PHOTO_UPLOADED, {
        file_size_bytes: compressed.size,
        // Client-side compression only kicks in above the Vercel body limit
        // threshold, so report what actually happened rather than hardcoding.
        compressed: compressed.size < file.size,
        original_size_bytes: file.size,
      });

      // On success, mark complete and move to next step
      markUploadComplete();
      setStep("area");
    } catch (error) {
      console.error("[Design Page] Failed to upload image:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload image. Please try again.";
      setUploadError(message);
      setBackendImageUrl(null);
      // DO NOT move to area step on error - let user see error in PhotoUploader
    } finally {
      setIsUploading(false); // Stop loader
    }
  };

  // Best-effort: any failure just means no warning. Never blocks the flow.
  const photoCheckUrlRef = useRef<string | null>(null);
  const runPhotoCheck = async (imageUrl: string) => {
    photoCheckUrlRef.current = imageUrl;
    try {
      const res = await fetch("/api/designs/photo-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data?.yardFraction !== "number") return;
      const b = data.cropBox;
      const cropBox: CropRect | null =
        b && [b.x, b.y, b.width, b.height].every((v) => typeof v === "number")
          ? { x: b.x, y: b.y, w: b.width, h: b.height }
          : null;
      // Ignore results for a photo that has since been replaced.
      if (photoCheckUrlRef.current !== imageUrl) return;
      setPhotoCheck({
        url: imageUrl,
        yardFraction: data.yardFraction,
        cropBox,
      });
      capture(ANALYTICS_EVENTS.PHOTO_CHECKED, {
        yard_fraction: Math.round(data.yardFraction * 1000) / 1000,
        has_crop_suggestion: cropBox !== null,
        warned: data.yardFraction < MIN_YARD_FRACTION,
      });
    } catch (err) {
      console.warn("[Design Page] Photo check failed:", err);
    }
  };

  const handleFixOutline = () => {
    const polygons = (
      polygonData as
        | import("@/components/design/AreaSelector").PolygonData
        | null
    )?.polygons;
    if (!polygonData || !polygons?.length) return;
    const grown = growPolygonsToCoverage(polygons, FIX_MASK_TARGET_COVERAGE);
    setPolygonOverride((prev) => ({
      data: { ...polygonData, polygons: grown },
      version: (prev?.version ?? 0) + 1,
    }));
    setOutlineAutoGrown(true);
  };

  const handleAreaSelected = (mask: string) => {
    setSelectedArea(mask);
  };

  // Inline "replace photo" while staying on the mask step. Reuses the upload
  // pipeline (which clears downstream state + re-uploads), so the user never
  // gets bounced back to the upload screen.
  const replacePhotoInputRef = useRef<HTMLInputElement>(null);

  // When the current generation started (ms epoch), so we can record its real
  // duration on success and let the loader pace future runs to reality.
  const generationStartRef = useRef<number | null>(null);
  // Email captured by the pre-generation EmailModal, to be auto-sent once the
  // run lands on the result step. Garden-center (retailer) flows only — D2C
  // keeps the manual send behind the stock-warning popup (PLA2-344).
  const pendingAutoEmailRef = useRef<string | null>(null);

  const openReplacePhotoPicker = () => {
    replacePhotoInputRef.current?.click();
  };

  const handleReplacePhotoClick = () => {
    if (isUploading) return;
    // Only interrupt with a confirm if there's drawn work to lose.
    if (selectedArea) {
      showConfirm(
        "Replace photo? The design area you've drawn will be cleared.",
        openReplacePhotoPicker,
        "Replace Photo"
      );
    } else {
      openReplacePhotoPicker();
    }
  };

  const handleReplacePhotoSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked later
    if (file) handleFileUpload(file);
  };

  // PLA2-315: create the Project row as soon as we have image + mask, so funnel
  // state is persisted from here onward (the per-step effect above PATCHes
  // resumeState) and generation reuses this row instead of creating a duplicate.
  // Best-effort — on failure projectId stays null and generation creates the row.
  const createProjectEarly = async () => {
    if (projectId || !backendImageUrl || !selectedArea) return;
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          originalImageUrl: backendImageUrl,
          initialMaskData: polygonData || selectedArea,
          customerEmail: customerEmail || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.id) setProjectId(data.id);
      }
    } catch (err) {
      console.warn("[Design Page] Failed to create in-progress project:", err);
    }
  };

  // Plant quantities are derived later from the generated image (vision plant
  // count in the pipeline), so the mask is all we need here — go straight on.
  useEffect(() => {
    if (!selectedArea) {
      setMaskCoverage(null);
      return;
    }
    let cancelled = false;
    computeMaskCoverage(selectedArea)
      .then((c) => {
        if (!cancelled) setMaskCoverage(c);
      })
      .catch(() => {
        // Fail open: an unreadable mask should never block the flow.
        if (!cancelled) setMaskCoverage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedArea]);

  const isMaskTooSmall =
    maskCoverage !== null && maskCoverage < MIN_MASK_COVERAGE;
  const showPhotoCheckWarning =
    !photoCheckDismissed &&
    photoCheck !== null &&
    photoCheck.url === backendImageUrl &&
    photoCheck.yardFraction < MIN_YARD_FRACTION;
  const isMaskSmall =
    maskCoverage !== null &&
    !isMaskTooSmall &&
    maskCoverage < SMALL_MASK_COVERAGE;

  const handleContinueFromArea = () => {
    if (selectedArea && !isMaskTooSmall) {
      setCompletedSteps((prev) => new Set(prev).add("area"));
      capture(ANALYTICS_EVENTS.MASK_DRAWN, {
        has_mask: true,
        mask_coverage:
          maskCoverage !== null ? Math.round(maskCoverage * 1000) / 1000 : null,
        outline_auto_grown: outlineAutoGrown,
        outline_kind: outlineKind,
        yard_fraction: photoCheck?.yardFraction ?? null,
        polygon_count: Array.isArray(
          (polygonData as { polygons?: unknown[] } | null)?.polygons
        )
          ? (polygonData as { polygons: unknown[] }).polygons.length
          : null,
      });
      void createProjectEarly();
      setStep("preferences");
    }
  };

  const handleZipcodeSubmit = async (zipcode: string) => {
    setUserZipcode(zipcode);

    // PLA2-688: stop before any design work if this retailer can't ship to the
    // ZIP's state. Unresolvable ZIPs return null and fall through untouched.
    const restriction = getShipRestriction(selectedRetailerId, zipcode);
    setShipRestriction(restriction);
    if (restriction) {
      capture(ANALYTICS_EVENTS.SHIP_RESTRICTED_SHOWN, {
        state: restriction.state,
        zipcode,
      });
      registerSuperProps({ zipcode });
      setStep("shipRestricted");
      return;
    }

    // Fetch user's hardiness zone for recipe filtering
    let resolvedZone: string | null = userZone;
    try {
      const zoneRes = await fetch(`/api/location?zipcode=${zipcode}`);
      if (zoneRes.ok) {
        const zoneData = await zoneRes.json();
        if (zoneData.zone) {
          setUserZone(zoneData.zone);
          resolvedZone = zoneData.zone;
        }
      }
    } catch (err) {
      console.warn(
        "[Design Page] Failed to fetch zone for recipe filtering:",
        err
      );
    }

    try {
      // If we are locked to a specific retailer via URL, validate territory
      if (isRetailerLocked) {
        const checkRes = await fetch("/api/territory/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ zipcode, retailerId: selectedRetailerId }),
        });
        const checkData = await checkRes.json();

        // Fire the funnel's ZIP step for retailer-locked traffic too. This
        // branch returns/throws before reaching the D2C capture below, so
        // without this every garden-center session was missing from the ZIP
        // step of the funnel — and territory rejections were never recorded.
        capture(ANALYTICS_EVENTS.ZIPCODE_ENTERED, {
          zone: resolvedZone,
          zipcode,
          territory_valid: !!checkData.hasTerritory,
          handoff: false,
        });
        registerSuperProps({ zone: resolvedZone, zipcode });

        if (!checkData.hasTerritory) {
          // THROW ERROR AND STOP HERE - this will be caught by ZipcodeEntry component
          throw new Error(
            `Sorry, your zip code is outside our service area for this retailer. Send an email to design-help@plantista.ai with your zip code, and they will work to connect you with someone who does.`
          );
        }

        // Only proceed if territory is valid
        setCompletedSteps((prev) => new Set(prev).add("preferences"));
        setStep("style");
        return;
      }

      // D2C channel-conflict guard (PLA2-318): if a partner garden center serves
      // this ZIP, hand the consumer off to that partner's Designer instead of
      // fulfilling D2C. Otherwise continue in D2C (Plantista storefront). Gated
      // here at the ZIP step — before any generation is spent.
      const response = await fetch("/api/retailers/nearby", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zipcode }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to find retailers");
      }

      // Nearest in-territory partner to hand off to. Every entry in `nearby` has
      // already been filtered by /api/retailers/nearby to live, non-pro accounts
      // carrying the AI_DESIGNER feature — i.e. they all have a Designer — so the
      // nearest one wins. (This used to additionally require a `designerDomain`,
      // which is null for every retailer in production and made the whole handoff
      // dead code: in-territory D2C sessions silently stayed on D2C.)
      const partner = (data.nearby || [])[0] as
        | { id: string; businessName: string }
        | undefined;

      capture(ANALYTICS_EVENTS.ZIPCODE_ENTERED, {
        zone: resolvedZone,
        zipcode,
        // D2C serves every ZIP; a partner match is a channel handoff, not a
        // territory failure — keep the two signals distinct.
        territory_valid: true,
        handoff: !!partner,
      });

      // Carry zone + zipcode onto every subsequent event this session (the
      // generation and checkout events need them for the admin breakdowns).
      registerSuperProps({ zone: resolvedZone, zipcode });

      if (partner) {
        setHandoffPartner({
          id: partner.id,
          name: partner.businessName,
        });
        setStep("handoff");
        return;
      }

      // Out of partner territory → continue in D2C.
      await handleSelectGeneral(zipcode);
    } catch (error) {
      console.error("[Design Page] Error in zipcode submission:", error);

      // If it's a territory validation error (for locked retailers), re-throw to show in UI
      if (
        isRetailerLocked &&
        error instanceof Error &&
        error.message.includes("outside our service area")
      ) {
        throw error; // Re-throw to ZipcodeEntry component to display
      }

      // For other errors in normal flow, fallback to general designer
      await handleSelectGeneral(zipcode);
    }
  };

  const handleSelectRetailer = async (
    selectedRetailer: string,
    zipcode: string
  ) => {
    try {
      // Update selected retailer ID in state
      setSelectedRetailerId(selectedRetailer);
      setRetailerSelected(true); // Mark that a retailer has been selected - triggers branding fetch
      // Committing to a partner garden center ends the D2C flow: without this,
      // a `/start` → `?d2c=1` entry keeps entryD2C sticky, so `isD2C` stays true
      // and the session would carry Plantista branding, the D2C value nuggets
      // and Shopify commerce into a retailer-branded design.
      setEntryD2C(false);
      console.log("[Design Page] Selected retailer:", selectedRetailer);

      // Save to session
      await fetch("/api/session/select-retailer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retailerId: selectedRetailer,
          zipcode,
          mode: "retailer",
        }),
      });

      // Mark zipcode step complete and continue
      // Note: branding will be fetched by the useEffect that depends on retailerSelected
      setCompletedSteps((prev) => new Set(prev).add("preferences"));
      setStep("style");
    } catch (error) {
      console.error("[Design Page] Error selecting retailer:", error);
      // Continue anyway
      setRetailerSelected(true);
      setCompletedSteps((prev) => new Set(prev).add("preferences"));
      setStep("style");
    }
  };

  const handleSelectGeneral = async (zipcode: string) => {
    try {
      // Update to default retailer in state
      setSelectedRetailerId(DEFAULT_RETAILER_ID);
      setRetailerSelected(true); // Mark that selection is complete (even if general)
      console.log("[Design Page] Using general designer (default retailer)");

      // Save to session with default retailer
      await fetch("/api/session/select-retailer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retailerId: DEFAULT_RETAILER_ID,
          zipcode,
          mode: "general",
        }),
      });
    } catch (error) {
      console.error("[Design Page] Error saving session:", error);
      setRetailerSelected(true); // Still mark as selected even on error
    }

    // Continue to style
    setCompletedSteps((prev) => new Set(prev).add("preferences"));
    setStep("style");
  };

  // Selecting a recipe advances straight to the plants step — no separate
  // "Continue" click. Pass the id directly since setSelectedStyle is async.
  const handleStyleSelected = (styleId: string) => {
    // Defense in depth: never let a sold-out recipe be selected (the Select
    // button is already disabled in StylePicker, but guard here too).
    if (recipeAvailability?.[styleId]?.soldOut === true) return;

    // recipe_selected / recipe_changed (ANALYTICS_SPEC §2). recipe_name is what
    // makes the "most popular recipes" breakdown readable — an id alone is not.
    const picked = recipes.find((r) => r.id === styleId);
    if (selectedStyle && selectedStyle !== styleId) {
      capture(ANALYTICS_EVENTS.RECIPE_CHANGED, {
        from_recipe_id: selectedStyle,
        to_recipe_id: styleId,
        to_recipe_name: picked?.name || null,
      });
    }
    capture(ANALYTICS_EVENTS.RECIPE_SELECTED, {
      recipe_id: styleId,
      recipe_name: picked?.name || null,
    });

    // A different recipe starts with no swaps.
    if (styleId !== selectedStyle) setPlantSubstitutions({});
    setSelectedStyle(styleId);
    setCompletedSteps((prev) => new Set(prev).add("style"));

    // Some of this recipe's plants are sold out → "Looking for alternatives"
    // before the plants step. The poll that told us is up to minutes old; the
    // dialog's own read is fresh and has the final word.
    const hasSoldOut = (picked?.genusAliases ?? []).some(
      (alias) =>
        plantAvailability[alias.id] === false || gateSoldOutIds.has(alias.id)
    );
    if (isEcommerce && hasSoldOut) {
      openAlternatives(styleId, picked?.name || "this style", "select");
      return;
    }
    setStep("plants");
  };

  // Fresh stock read + scored substitutes for one recipe. Fails closed: if the
  // store can't be reached the dialog offers Retry / Back, never unverified
  // plants.
  const openAlternatives = async (
    recipeId: string,
    recipeName: string,
    trigger: "select" | "gate",
    plantIds?: string[]
  ) => {
    setAlternativesDialog({
      recipeId,
      recipeName,
      trigger,
      status: "loading",
      result: null,
      plantIds,
    });
    try {
      const res = await fetch("/api/commerce/availability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retailerId: commerceTenantId,
          alternatives: {
            catalogRetailerId: retailerId,
            recipeId,
            zone: userZone,
            plantIds,
          },
        }),
      });
      if (!res.ok) throw new Error(`alternatives HTTP ${res.status}`);
      const result = (await res.json()) as RecipeAlternativesResponse;

      // Stock came back since the poll: nothing to swap, carry on.
      if (result.missing.length === 0) {
        setAlternativesDialog(null);
        setStep("plants");
        return;
      }
      setAlternativesDialog((prev) =>
        prev && prev.recipeId === recipeId
          ? { ...prev, status: "ready", result }
          : prev
      );
      capture(ANALYTICS_EVENTS.SUBSTITUTES_SHOWN, {
        recipe_id: recipeId,
        recipe_name: recipeName,
        trigger,
        missing_count: result.missing.length,
        with_options_count: result.missing.filter(
          (m) => m.alternatives.length > 0
        ).length,
        recipe_pulled_before: result.pulledBefore,
      });
    } catch (err) {
      console.error("[Design Page] Alternatives lookup failed:", err);
      setAlternativesDialog((prev) =>
        prev && prev.recipeId === recipeId
          ? { ...prev, status: "error", result: null }
          : prev
      );
    }
  };

  const handleAlternativesConfirm = (choice: AlternativesChoice) => {
    const dialog = alternativesDialog;
    if (!dialog?.result) return;
    const { result, trigger, recipeId } = dialog;

    // Map each missing plant back to the recipe plant it stands for: on a gate
    // retry the sold-out plant may itself be an earlier substitute.
    const next: Record<string, Plant> =
      trigger === "select" ? {} : { ...plantSubstitutions };
    const removeIds = new Set<string>();
    const addIds: string[] = [];
    let accepted = 0;
    for (const missing of result.missing) {
      const sub = choice[missing.plantId] ?? null;
      const originalId =
        Object.keys(next).find((k) => next[k].id === missing.plantId) ??
        missing.plantId;
      removeIds.add(missing.plantId);
      if (sub) {
        next[originalId] = designPlantToPlant(sub);
        addIds.push(sub.id);
        accepted++;
        const rank = missing.alternatives.findIndex(
          (a) => a.plant.id === sub.id
        );
        capture(ANALYTICS_EVENTS.SUBSTITUTE_ACCEPTED, {
          recipe_id: recipeId,
          trigger,
          original_plant_id: missing.plantId,
          original_plant_name: missing.name,
          substitute_plant_id: sub.id,
          substitute_plant_name: sub.commonName,
          rank: rank >= 0 ? rank + 1 : null,
          score: rank >= 0 ? missing.alternatives[rank].score : null,
          was_default: sub.id === missing.defaultId,
        });
      } else {
        delete next[originalId];
      }
    }
    if (
      result.pulledBefore &&
      !isRecipePulled(
        Math.min(result.total, result.available + accepted),
        result.total,
        result.overridden
      )
    ) {
      capture(ANALYTICS_EVENTS.RECIPE_AUTO_HEALED, {
        recipe_id: recipeId,
        swaps: accepted,
        total: result.total,
        available_before: result.available,
      });
    }

    substituteIdsRef.current = new Set(Object.values(next).map((p) => p.id));
    setPlantSubstitutions(next);
    setAvailablePlants((prev) => {
      const have = new Set(prev.map((p) => p.id));
      const add = Object.values(next).filter((p) => !have.has(p.id));
      return add.length > 0 ? [...prev, ...add] : prev;
    });
    setSelectedPlants((prev) =>
      withSubstitutesFirst(
        prev.filter((id) => !removeIds.has(id)),
        addIds
      )
    );
    setAlternativesDialog(null);
    setStep("plants");
  };

  // substitute id → sold-out plant name, for the "Replaces …" card badge.
  const substitutedFor = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [originalId, sub] of Object.entries(plantSubstitutions)) {
      const original = availablePlants.find((p) => p.id === originalId);
      out[sub.id] = original?.commonName ?? "a sold-out plant";
    }
    return out;
  }, [plantSubstitutions, availablePlants]);

  // substitute id → the sold-out plant it replaced, for the shopping list.
  const replacedFor = useMemo(() => {
    const out: Record<string, Plant> = {};
    for (const [originalId, sub] of Object.entries(plantSubstitutions)) {
      const original = availablePlants.find((p) => p.id === originalId);
      if (original) out[sub.id] = original;
    }
    return out;
  }, [plantSubstitutions, availablePlants]);

  // Back to styles from the alternatives step (pick another recipe).
  const handleAlternativesBack = () => {
    setAlternativesDialog(null);
    setPlantSubstitutions({});
    setSelectedStyle(null);
    setSelectedRecipe(null);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.delete("style");
      next.delete("plants");
      return next;
    });
    setStep("style");
  };

  // Substitutes must survive the recipe-plants fetch (it replaces
  // availablePlants with the recipe's own plants) and session restore.
  useEffect(() => {
    const subs = Object.values(plantSubstitutions);
    if (subs.length === 0 || availablePlants.length === 0) return;
    const have = new Set(availablePlants.map((p) => p.id));
    const add = subs.filter((p) => !have.has(p.id));
    if (add.length > 0) setAvailablePlants((prev) => [...prev, ...add]);
  }, [plantSubstitutions, availablePlants]);

  // PLA2-613: the pull rule is only enforced at selection time above, so a
  // recipe restored from the session (or picked before stock dropped) can sit
  // on the plants step after it's been pulled. Send the user back to styles.
  useEffect(() => {
    if (step !== "plants" || !selectedStyle) return;
    if (recipeAvailability[selectedStyle]?.soldOut !== true) return;
    setSelectedStyle(null);
    setSelectedRecipe(null);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.delete("style");
      next.delete("plants");
      return next;
    });
    setStep("style");
    showInfo(
      "Most of the plants in the garden style you picked are no longer available. Please choose another style.",
      "Style no longer available"
    );
  }, [step, selectedStyle, recipeAvailability, showInfo]);

  // Ref mirrors of state so the plants-fetch effect can read latest values
  // without adding them as deps (which would loop each time we set them).
  const selectedRecipeRef = useRef(selectedRecipe);
  useEffect(() => {
    selectedRecipeRef.current = selectedRecipe;
  }, [selectedRecipe]);
  const availablePlantsRef = useRef(availablePlants);
  useEffect(() => {
    availablePlantsRef.current = availablePlants;
  }, [availablePlants]);
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // Fetch recipe plants when style is selected
  useEffect(() => {
    const fetchRecipePlants = async () => {
      if (!selectedStyle) return;

      // Resolve the recipe from the loaded list, or fall back to the
      // session-restored `selectedRecipe` (read via ref so we don't depend
      // on its identity and loop when we setSelectedRecipe below).
      const restoredRecipe = selectedRecipeRef.current;
      const recipe =
        recipes.find((r) => r.id === selectedStyle) ||
        (restoredRecipe?.id === selectedStyle ? restoredRecipe : null);

      if (!recipe) {
        // Still waiting on the recipes list — the effect will re-fire once
        // it loads. If recipes have loaded and the id genuinely isn't there
        // (e.g. retailer removed it), log a warning and stop.
        if (recipes.length === 0) return;
        console.warn(
          "[Design Page] Recipe not found in loaded recipes (id:",
          selectedStyle,
          ") — may have been removed. Skipping plant fetch."
        );
        return;
      }

      // On the result page, if we already have plants (from session restore
      // or a prior fetch), don't re-fetch — the recipe may have been removed
      // server-side and we'd just 404 and wipe good data.
      if (
        stepRef.current === "result" &&
        availablePlantsRef.current.length > 0
      ) {
        return;
      }

      setLoadingPlants(true);
      try {
        console.log("[Design Page] Fetching plants for recipe:", selectedStyle);

        // Only update selectedRecipe when it actually differs — avoids
        // creating a new reference on every effect run.
        if (
          !restoredRecipe ||
          restoredRecipe.id !== recipe.id ||
          restoredRecipe.name !== recipe.name
        ) {
          const recipeInfo: RecipeInfo = {
            id: recipe.id,
            name: recipe.name || "Untitled Recipe",
            description: recipe.description || "",
            imageUrl: recipe.imageUrl || "",
            style: recipe.style || "cottage",
            sunLevel: recipe.sunLevel || "full_sun",
            usableZones: recipe.usableZones,
          };
          setSelectedRecipe(recipeInfo);
        }
        console.log("[Design Page] Recipe loaded:", recipe.name);

        // Fetch plants for this recipe
        const plantsUrl = `/api/recipes/${selectedStyle}/plants?retailerId=${retailerId}`;
        console.log("[Design Page] Fetching plants from:", plantsUrl);
        const plantsRes = await fetch(plantsUrl);
        if (!plantsRes.ok) {
          throw new Error("Failed to fetch recipe plants");
        }
        const plants = await plantsRes.json();
        setAvailablePlants(plants);
        console.log("[Design Page] Loaded", plants.length, "plants");

        // Auto-select all plants only if no existing selection (avoid overwriting session restore).
        // Out-of-stock plants are never auto-selected (PLA2-458); read via ref so
        // this effect doesn't re-run whenever availability refreshes.
        setSelectedPlants((prev) => {
          const plantIds = new Set(plants.map((p: Plant) => p.id));
          const soldOut = soldOutPlantIdsRef.current;
          const hasValidSelection =
            prev.length > 0 && prev.some((id) => plantIds.has(id));
          // Capped at MAX_SELECTED_PLANTS (PLA2-762): recipes can carry more
          // plants than we generate with, so the rest show up unchecked.
          // Substitutes picked in the alternatives step aren't recipe plants
          // but belong in the selection all the same.
          const subs = substituteIdsRef.current;
          return hasValidSelection
            ? withSubstitutesFirst(
                prev.filter(
                  (id) => plantIds.has(id) && !subs.has(id) && !soldOut.has(id)
                ),
                prev.filter((id) => subs.has(id) && !soldOut.has(id))
              )
            : withSubstitutesFirst(
                plants
                  .filter((p: Plant) => !soldOut.has(p.id))
                  .map((p: Plant) => p.id),
                [...subs]
              );
        });
      } catch (error) {
        console.error("[Design Page] Failed to fetch recipe plants:", error);
        // Only clear availablePlants if we don't already have data — a
        // failed refetch shouldn't wipe plants that survived via session
        // restore (e.g. when the retailer deleted the recipe after design).
        if (availablePlantsRef.current.length === 0) {
          setAvailablePlants([]);
        }
        // Preserve selectedPlants so a transient failure doesn't wipe the
        // user's restored selection. The auto-select block above will
        // reconcile it once plants load successfully.
      } finally {
        setLoadingPlants(false);
      }
    };

    fetchRecipePlants();
  }, [selectedStyle, recipes, retailerId]);

  // Auto-hide the plant-limit notice a few seconds after it appears.
  useEffect(() => {
    if (!plantLimitNotice) return;
    const timer = setTimeout(() => setPlantLimitNotice(false), 6000);
    return () => clearTimeout(timer);
  }, [plantLimitNotice]);

  const handleTogglePlant = (plantId: string) => {
    // Decide + capture outside the state updater: React invokes updaters twice
    // under StrictMode, which would double-fire the analytics event.
    const wasSelected = selectedPlants.includes(plantId);
    // Deselect always works; selecting an out-of-stock plant is ignored
    // (PLA2-458) — the card is also rendered disabled. Bail before the
    // analytics capture so an ignored click never fires plant_added.
    if (!wasSelected && soldOutPlantIds.has(plantId)) {
      return;
    }
    // Block the 13th pick with an explanation instead of silently ignoring
    // the click (PLA2-762).
    if (!wasSelected && selectedPlants.length >= MAX_SELECTED_PLANTS) {
      setPlantLimitNotice(true);
      return;
    }
    setPlantLimitNotice(false);
    const plant = availablePlants.find((p) => p.id === plantId);
    capture(
      wasSelected
        ? ANALYTICS_EVENTS.PLANT_REMOVED
        : ANALYTICS_EVENTS.PLANT_ADDED,
      {
        genus_alias_id: plantId,
        genus_name: plant?.commonName || null,
        recipe_id: selectedStyle || null,
      }
    );

    setSelectedPlants((prev) =>
      prev.includes(plantId)
        ? prev.filter((id) => id !== plantId)
        : prev.length >= MAX_SELECTED_PLANTS
          ? prev
          : [...prev, plantId]
    );
  };

  const handleGenerateClick = () => {
    if (serviceUnavailable) return;
    // Show email modal before generation
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = (email: string, consent: boolean) => {
    // Store email + marketing consent, then close modal
    setCustomerEmail(email);
    setMarketingConsent(consent);
    setIsEmailModalOpen(false);
    // Start generation. Consent is held in state + sessionStorage and sent with
    // the design email (auto-sent on completion for garden-center flows).
    handleGenerate(email);
  };

  const pollJobStatus = async (jobId: string, email: string) => {
    let pollCount = 0;
    const maxPolls = 300; // 10 minutes max (2 second intervals)

    const poll = async (): Promise<void> => {
      try {
        pollCount++;
        if (pollCount > maxPolls) {
          throw new Error(
            "Generation timed out. Please check your email for results or try again."
          );
        }

        const response = await fetch(`/api/jobs/${jobId}/status`);
        if (!response.ok) {
          throw new Error("Failed to check job status");
        }

        const status = await response.json();
        console.log("[Design Page] Job status:", status);

        // Update UI state
        setJobStatus(status.status);
        setJobProgress(status.progress || 0);
        setQueuePosition(status.queuePosition);
        setEstimatedWait(status.estimatedWaitSeconds);

        if (status.status === "completed" && status.results) {
          // Generation complete!
          console.log("[Design Page] Generation completed:", status.results);

          // Store project ID
          if (status.results.projectId) {
            setProjectId(status.results.projectId);
          }

          // Store full results — strip presigned params so URLs survive session restore
          setGenerationResults({
            spring: toPermanentUrl(status.results.spring),
            summer: toPermanentUrl(status.results.summer),
            autumn: toPermanentUrl(status.results.autumn),
            winter: toPermanentUrl(status.results.winter),
            gif: toPermanentUrl(status.results.gif),
            diagramUrl: toPermanentUrl(status.results.diagramUrl),
          });

          // Clear stale edit results from previous generations
          setEditedResults({});
          setEditHistories({});

          // Set generated image URL (summer is the baseline/hero image;
          // fall back to spring for any in-flight/old jobs still keyed on it)
          const heroUrl = status.results.summer || status.results.spring;
          if (heroUrl) {
            setGeneratedImageUrl(toPermanentUrl(heroUrl));
          }

          // Plant counts come from the pipeline's vision count of the generated
          // image (backend keys by plant.alias, frontend by plant.id). Unknown
          // plants fall back to a "1+" estimate at display time.
          if (status.results.plantCounts) {
            setPlantCounts(
              mapCountsByPlantId(status.results.plantCounts, availablePlants)
            );
          }

          setIsGenerating(false);
          setStep("result");
          // Record how long this run actually took so the loader paces itself
          // to reality on the next generation.
          if (generationStartRef.current !== null) {
            recordGenerationDuration(
              generateAllSeasons,
              (Date.now() - generationStartRef.current) / 1000
            );
            generationStartRef.current = null;
          }
          // NOTE: generation_succeeded is fired SERVER-SIDE (worker/main.py via
          // api/services/analytics.py) so elapsed_ms is the real generation time
          // and generation_failed also gets recorded. Do not re-add a client
          // capture here — it would double-count.
          capture(
            ANALYTICS_EVENTS.DESIGN_COMPLETED,
            {
              recipe_id: selectedStyle || null,
              recipe_name: selectedRecipe?.name || null,
              seasons_generated: generateAllSeasons ? 4 : 1,
            },
            markDesignCompleted(isD2C ? "d2c" : "gc")
          );

          // Garden-center flows auto-send the design email once the result
          // step renders (see the pendingAutoEmailRef effect).

          return; // Stop polling
        } else if (status.status === "failed") {
          throw new Error(status.error || "Generation failed");
        } else {
          // Still queued/processing - continue polling
          setTimeout(poll, 2000); // Poll every 2 seconds
        }
      } catch (error) {
        console.error("[Design Page] Polling error:", error);
        setIsGenerating(false);
        showError(
          error instanceof Error
            ? error.message
            : "Failed to check generation status",
          "Generation Failed"
        );
        setStep("plants");
      }
    };

    // Start polling
    await poll();
  };

  const handleGenerate = async (email: string) => {
    // PLA2-688: never spend a generation on a ZIP the retailer can't ship to
    // (e.g. a session restored past the ZIP step).
    const restriction = getShipRestriction(selectedRetailerId, userZipcode);
    if (restriction) {
      setShipRestriction(restriction);
      setStep("shipRestricted");
      return;
    }
    setIsGenerating(true);
    generationStartRef.current = Date.now();
    pendingAutoEmailRef.current = email && !isD2C ? email : null;
    setCompletedSteps((prev) => new Set(prev).add("plants"));
    setStep("generate");

    // plants_selected + generation_started (PLA2-366)
    capture(ANALYTICS_EVENTS.PLANTS_SELECTED, {
      plant_count: selectedPlants.length,
      recipe_id: selectedStyle || null,
      recipe_name: selectedRecipe?.name || null,
    });
    capture(ANALYTICS_EVENTS.GENERATION_STARTED, {
      plant_count: selectedPlants.length,
      recipe_id: selectedStyle || null,
      recipe_name: selectedRecipe?.name || null,
      generate_all_seasons: generateAllSeasons,
    });

    // Clear previous generation results so stale images don't show
    setGenerationResults(null);
    setGeneratedImageUrl(null);
    setPlantCounts({});
    setEditedResults({});
    setEditHistories({});

    // Reset async state
    setJobId(null);
    setJobStatus("queued");
    setJobProgress(0);
    setQueuePosition(undefined);
    setEstimatedWait(undefined);

    try {
      console.log("[Design Page] Starting async generation...");
      console.log("[Design Page] Backend image URL:", backendImageUrl);
      console.log("[Design Page] Selected plants:", selectedPlants);
      console.log("[Design Page] Mask data length:", selectedArea?.length || 0);

      if (!backendImageUrl) {
        throw new Error("Image not uploaded to backend yet");
      }

      if (!selectedArea) {
        throw new Error(
          "Please draw an area on your image to indicate where to add plants"
        );
      }

      // Get the selected plant details for logging
      const selectedPlantNames = availablePlants
        .filter((p) => selectedPlants.includes(p.id))
        .map((p) => p.commonName);
      console.log("[Design Page] Selected plant names:", selectedPlantNames);

      // Build the payload - always send plantIds (user may have customized selection).
      // Defense in depth on top of the auto-deselect effect: never generate a
      // design around an out-of-stock plant (PLA2-458).
      const payload = {
        landscapeUrl: backendImageUrl,
        plantIds: selectedPlants.filter((id) => !soldOutPlantIds.has(id)),
        recipeId: selectedStyle, // Include recipe ID for database tracking
        recipeImageUrl: selectedRecipe?.imageUrl || undefined,
        maskData: selectedArea,
        maskColor: "#FF0000", // Red — solid red mask for AI (UI shows green)
        polygonData: polygonData || undefined,
        generateAllSeasons,
        // PLA2-553: the planting diagram is part of the take-away plan. It is
        // keyed off the same plant counts as the shopping list (countPlants is
        // always on), so the key quantities match the list and cart.
        generateDiagram: true,
        countPlants,
        customerEmail: email,
        // PLA2-315: reuse the in-progress Project row (created after area select)
        // so generation updates it instead of creating a duplicate.
        projectId: projectId || undefined,
        // Analytics attribution: the backend fires generation_succeeded /
        // generation_failed and needs these to land the event on this session's
        // PostHog person and retailer group. Both are optional — the backend
        // falls back to a hashed customerEmail, then skips.
        analyticsDistinctId: getDistinctId() || undefined,
        retailerId: retailerId || undefined,
        userZone: userZone || undefined,
        userZipcode: userZipcode || undefined,
        // PLA2-687: the server re-checks live stock for selling stores and
        // refuses to generate around anything the store can't sell.
        commerceTenantId: isEcommerce
          ? commerceTenantId || undefined
          : undefined,
        // PLA2-613: the recipe's full plant list, so the gate can re-apply the
        // >50%-unavailable pull rule on its fresh read.
        recipePlantIds: isEcommerce
          ? recipes
              .find((r) => r.id === selectedStyle)
              ?.genusAliases?.map((alias) => alias.id)
          : undefined,
        // Out-of-stock alternatives: original → substitute for the swaps still
        // in the selection, so the gate evaluates the pull rule after swapping.
        substitutions: isEcommerce
          ? Object.fromEntries(
              Object.entries(plantSubstitutions)
                .filter(([, sub]) => selectedPlants.includes(sub.id))
                .map(([originalId, sub]) => [originalId, sub.id])
            )
          : undefined,
      };

      console.log("[Design Page] Sending payload:", {
        ...payload,
        maskData: `<base64 PNG, ${payload.maskData.length} chars>`,
      });

      // Use sync endpoint for local testing (set NEXT_PUBLIC_USE_SYNC_GENERATION=true in .env.local)
      const useSyncGeneration =
        process.env.NEXT_PUBLIC_USE_SYNC_GENERATION === "true";
      const generateEndpoint = useSyncGeneration
        ? "/api/designs/generate"
        : "/api/designs/generate-async";
      console.log(
        `[Design Page] Using ${useSyncGeneration ? "SYNC" : "ASYNC"} generation endpoint: ${generateEndpoint}`
      );

      const response = await fetch(generateEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Async proxy returns `{ error }`, sync proxy returns `{ detail }`.
        const errorData = await response.json().catch(() => ({}));
        // Stock gate (PLA2-687): mark the rejected plants sold out — the
        // deselect effect drops them and the cards render disabled — then fall
        // through to the error below, which returns the user to the plants step.
        if (
          (errorData.code === "PLANTS_UNAVAILABLE" ||
            errorData.code === "RECIPE_UNAVAILABLE") &&
          Array.isArray(errorData.unavailablePlantIds)
        ) {
          const rejected: string[] = errorData.unavailablePlantIds;
          setGateSoldOutIds((prev) => new Set([...prev, ...rejected]));
        }
        // Plants sold out between browsing and Generate: offer in-stock
        // alternatives for exactly those plants instead of just dropping them.
        if (
          errorData.code === "PLANTS_UNAVAILABLE" &&
          isEcommerce &&
          selectedStyle
        ) {
          setIsGenerating(false);
          setStep("plants");
          openAlternatives(
            selectedStyle,
            selectedRecipe?.name || "this style",
            "gate",
            payload.plantIds
          );
          return;
        }
        // PLA2-613: the recipe itself was pulled — trimming plants can't fix
        // it, so send the user back to pick another style.
        if (errorData.code === "RECIPE_UNAVAILABLE") {
          setIsGenerating(false);
          setSelectedStyle(null);
          setSelectedRecipe(null);
          setCompletedSteps((prev) => {
            const next = new Set(prev);
            next.delete("style");
            next.delete("plants");
            return next;
          });
          setStep("style");
          showError(
            errorData.error ||
              "This garden style is no longer available. Please pick another style.",
            "Style no longer available"
          );
          return;
        }
        const detail =
          typeof errorData.detail === "string"
            ? errorData.detail
            : errorData.detail
              ? JSON.stringify(errorData.detail)
              : undefined;
        throw new Error(
          errorData.error ||
            detail ||
            `Failed to create generation job (HTTP ${response.status})`
        );
      }

      const result = await response.json();
      console.log("[Design Page] Generation result:", result);

      if (useSyncGeneration) {
        // Sync endpoint returns results directly
        console.log("[Design Page] Sync generation complete:", result);
        setGenerationResults({
          spring: toPermanentUrl(result.spring || result.springUrl),
          summer: toPermanentUrl(result.summer || result.summerUrl),
          autumn: toPermanentUrl(result.autumn || result.autumnUrl),
          winter: toPermanentUrl(result.winter || result.winterUrl),
          gif: toPermanentUrl(result.gif || result.gifUrl),
          diagramUrl: toPermanentUrl(result.diagramUrl),
        });
        if (result.projectId) setProjectId(result.projectId);
        // Plant counts come from the pipeline's vision count of the generated
        // image. Unknown plants fall back to a "1+" estimate at display time.
        if (result.plantCounts) {
          setPlantCounts(
            mapCountsByPlantId(result.plantCounts, availablePlants)
          );
        }
        // Summer is the baseline/hero; fall back to spring for old payloads
        setGeneratedImageUrl(
          toPermanentUrl(
            result.summer ||
              result.summerUrl ||
              result.spring ||
              result.springUrl
          )
        );
        setIsGenerating(false);
        setStep("result");
        // Record how long this run actually took so the loader paces itself
        // to reality on the next generation.
        if (generationStartRef.current !== null) {
          recordGenerationDuration(
            generateAllSeasons,
            (Date.now() - generationStartRef.current) / 1000
          );
          generationStartRef.current = null;
        }
        // generation_succeeded is fired server-side (api/routes/generate.py) —
        // see the note on the async path above. Do not re-add it here.
        capture(
          ANALYTICS_EVENTS.DESIGN_COMPLETED,
          {
            recipe_id: selectedStyle || null,
            recipe_name: selectedRecipe?.name || null,
            seasons_generated: generateAllSeasons ? 4 : 1,
          },
          markDesignCompleted(isD2C ? "d2c" : "gc")
        );

        // Send email if provided
        if (email) {
          setCustomerEmail(email);
        }
      } else {
        // Async endpoint returns jobId — start polling
        const createdJobId = result.jobId;
        setJobId(createdJobId);
        setQueuePosition(result.queuePosition);
        setEstimatedWait(result.estimatedTime);

        // Start polling for job status
        await pollJobStatus(createdJobId, email);

        // pollJobStatus will handle the rest
        // (progress updates, completion, email sending, etc.)
      }
    } catch (error) {
      console.error("[Design Page] Generation failed:", error);
      setIsGenerating(false);
      showError(
        error instanceof Error
          ? error.message
          : "Failed to generate design. Please try again.",
        "Generation Failed"
      );
      setStep("plants"); // Go back to plants step
    }
  };

  const handleStepClick = (stepKey: Step) => {
    const targetIndex = progressSteps.findIndex((ps) => ps.key === stepKey);
    const currentIndex = progressSteps.findIndex(
      (ps) => ps.key === getProgressStep(step)
    );
    const goingBack = targetIndex < currentIndex;

    // When going backward, clear everything after the target step
    if (goingBack) {
      clearStepsAfter(targetIndex);
    }

    switch (stepKey) {
      case "upload":
        // Go to area (photo + mask) if photo exists, otherwise upload
        setStep(uploadedImageUrl || backendImageUrl ? "area" : "upload");
        break;
      case "area":
        // Go to preferences (zipcode entry) with zip pre-filled from state
        setStep("preferences");
        break;
      case "preferences":
        // Go to style picker (recipe selection)
        setStep("style");
        break;
      case "plants":
        // Go to plant list
        setStep("plants");
        break;
      case "result":
        // Go to result view
        setStep("result");
        break;
    }
  };

  const sendDesignEmail = async (
    email: string,
    currentProjectId?: string,
    seasonalResults?: {
      // summer is the always-present baseline; spring only in all-seasons mode
      summer: string;
      spring?: string;
      autumn: string;
      winter: string;
      gif: string;
    }
  ) => {
    try {
      console.log("[Design Page] Sending design email to:", email);

      // Use provided results or fall back to state
      const resultsToSend = seasonalResults || generationResults;
      const projectIdToUse = currentProjectId || projectId;

      if (!resultsToSend) {
        throw new Error("No generation results available");
      }

      if (!projectIdToUse) {
        throw new Error("Project ID not available");
      }

      // Build plant list with genus names, quantities, and care info
      const selectedPlantData = availablePlants.filter((p) =>
        selectedPlants.includes(p.id)
      );
      const plantList = selectedPlantData.map((p) => ({
        alias_name: p.commonName || p.scientificName || "Unknown Plant",
        genus_name: p.scientificName || p.commonName || "",
        // Preserve an honest 0 ("not used"); only a missing count → null.
        quantity: plantCounts[p.id] ?? null,
        // §5.5 fields for the design email (PLA2-352)
        image_url: p.imageUrl || undefined,
        sun_level: p.sunLevel
          ? p.sunLevel
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : undefined,
        water_needs: p.waterNeeds || undefined,
        zones: p.zones || undefined,
        care_instructions: p.careInstructions || undefined,
        height: p.height || undefined,
        width: p.width || undefined,
        qualities: p.description || undefined,
      }));

      // Fetch product recommendations based on selected plants' genus IDs.
      // Each product carries the retailer's description ("why") and the design
      // plants it applies to, ordered Planting → Fertilizing (PLA2-9). The
      // Plantista endpoint requires retailer_id — without it the list is empty.
      let productList: Array<{
        product_name: string;
        plant_names: string[];
        why: string;
        trigger_type: string;
      }> = [];
      try {
        const genusIds = selectedPlantData
          .map((p) => (p as any).genusId)
          .filter((id): id is string => !!id);

        if (tenant.features.productRecommendations && genusIds.length > 0) {
          const uniqueGenusIds = [...new Set(genusIds)];
          const retailerParam = retailerId ? `&retailerId=${retailerId}` : "";
          const recsResponse = await fetch(
            `/api/product-recommendations?genusIds=${uniqueGenusIds.join(",")}${retailerParam}`
          );
          if (recsResponse.ok) {
            const recommendations: ProductRecommendation[] =
              await recsResponse.json();
            const nameByGenusId: Record<string, string> = {};
            for (const p of selectedPlantData) {
              const genusId = (p as any).genusId;
              if (genusId && p.commonName)
                nameByGenusId[genusId] = p.commonName;
            }
            productList = groupRecommendationsByProduct(
              recommendations,
              nameByGenusId
            ).flatMap((section) =>
              section.products.map((product) => ({
                product_name: product.productName,
                plant_names: product.plantNames,
                why: product.description,
                trigger_type: product.triggerType,
              }))
            );
            console.log(
              "[Design Page] Product recommendations for email:",
              productList.length
            );
          }
        }
      } catch (error) {
        console.error(
          "[Design Page] Failed to fetch product recommendations for email:",
          error
        );
        // Continue with empty product list - don't fail the email
      }

      // Get DESIGN + PLANTING + FERTILIZING coupons (skip for Fossil Creek — special event handled separately)
      const FOSSIL_CREEK_ID = "e955b2c5-d47d-4ae4-971f-36ed0deb87f2";
      const emailCoupons: Array<{
        purpose: string;
        code: string | null;
        imageUrl: string | null;
        minPurchaseRequirement: number | null;
        relativeExpirationDays: number | null;
      }> = [];
      if (retailerId !== FOSSIL_CREEK_ID && retailerData?.coupons?.length > 0) {
        const ALLOWED_PURPOSES = ["DESIGN", "PLANTING", "FERTILIZING"];
        for (const c of retailerData.coupons) {
          if (!c.deleted && ALLOWED_PURPOSES.includes(c.purpose)) {
            emailCoupons.push({
              purpose: c.purpose,
              code: c.code || null,
              imageUrl: c.image_url || c.imageUrl || null,
              minPurchaseRequirement:
                c.min_purchase_requirement ?? c.minPurchaseRequirement ?? null,
              relativeExpirationDays:
                c.relative_expiration_days ?? c.relativeExpirationDays ?? null,
            });
          }
        }
      }

      const payload = {
        email,
        projectId: projectIdToUse,
        retailerId: retailerId,
        recipeId: selectedStyle,
        styleName: selectedRecipe?.name || undefined,
        sunLevel: selectedRecipe?.sunLevel || undefined,
        seasonalImages: {
          spring: resultsToSend.spring,
          summer: resultsToSend.summer,
          autumn: resultsToSend.autumn,
          winter: resultsToSend.winter,
          gif: resultsToSend.gif,
        },
        plantList,
        productList,
        coupons: emailCoupons,
        zipcode: userZipcode,
        zone: userZone || undefined,
        generationDate: new Date().toISOString(),
        // Explicit marketing opt-in (PLA2-373). Sent alongside the email so the
        // recipient's consent travels with the design email request.
        marketingConsent,
      };

      console.log("[Design Page] Email payload:", {
        ...payload,
        plantList: `${plantList.length} plants`,
        productList: `${productList.length} products`,
      });

      const response = await fetch("/api/email/send-design", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to send email");
      }

      console.log("[Design Page] Email sent successfully");

      // Update project record with customer email
      try {
        const updateResponse = await fetch(`/api/projects/${projectIdToUse}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerEmail: email, marketingConsent }),
        });

        if (updateResponse.ok) {
          console.log("[Design Page] Project updated with customer email");
        } else {
          console.warn("[Design Page] Failed to update project with email");
        }
      } catch (updateError) {
        console.warn("[Design Page] Error updating project:", updateError);
        // Non-critical error, don't throw
      }

      console.log("[Design Page] ✅ Design emailed successfully");
      // Coupon codes ride on the event (PLA2-10) so redemptions can eventually
      // be traced back to the design/retailer that generated them — coupon
      // codes are marketing codes, not PII, so this is safe alongside the
      // "no email address" rule above.
      capture(ANALYTICS_EVENTS.DESIGN_EMAIL_SENT, {
        seasons_count: generationResults?.summer ? 4 : 1,
        has_coupon: emailCoupons.length > 0,
        coupon_codes: emailCoupons
          .map((c) => c.code)
          .filter((code): code is string => !!code),
        coupon_purposes: emailCoupons.map((c) => c.purpose),
      });
      return true;
    } catch (error) {
      console.error("[Design Page] Failed to send email:", error);
      capture(ANALYTICS_EVENTS.DESIGN_EMAIL_FAILED, {
        error_code:
          error instanceof Error ? error.message.slice(0, 120) : "unknown",
      });
      // Don't throw - email is secondary, design was successful
      return false;
    }
  };

  const handleResendEmail = async () => {
    if (!customerEmail) {
      showError("No email address found. Please refresh and try again.");
      return;
    }

    try {
      const sent = await sendDesignEmail(customerEmail);
      if (!sent) throw new Error("Failed to send email. Please try again.");
      showSuccess("Your garden design has been resent to your email!");
    } catch (error) {
      console.error("[Design Page] Failed to resend email:", error);
      showError(
        error instanceof Error
          ? error.message
          : "Failed to send email. Please try again.",
        "Email Failed"
      );
      throw error;
    }
  };

  // Auto-send the design email for garden-center flows once generation lands
  // on the result step. Runs as an effect (not inside pollJobStatus) so
  // sendDesignEmail sees the committed results, projectId and plant counts
  // rather than the stale closure captured when generation started.
  useEffect(() => {
    const email = pendingAutoEmailRef.current;
    if (!email || step !== "result" || !generationResults) return;
    pendingAutoEmailRef.current = null;
    sendDesignEmail(email).then((sent) => {
      if (sent) showSuccess(`Your garden design has been sent to ${email}.`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, generationResults]);

  // PLA2-548/549/550/551: reconcile the shopping list (and, transitively, the
  // cart — both derive from selectedPlants + plantCounts) against an edit's
  // post-edit plant identification pass. Only fires when ResultViewer was
  // able to opt the edit into the plant-aware path (projectId +
  // per-season generationId); currently a no-op for the GC flow until that
  // generationId plumbing exists (see PLA2-548 follow-up notes).
  const handleEditPlantChanges = (result: PlantReconciliationResult) => {
    if (result.plantCounts) {
      setPlantCounts(mapCountsByPlantId(result.plantCounts, availablePlants));
    }
    const removedNames = result.plantChanges?.removed ?? [];
    const addedNames = result.plantChanges?.added ?? [];
    if (removedNames.length || addedNames.length) {
      const removedIds = new Set(
        removedNames
          .map((name) => findPlantIdByName(name, availablePlants))
          .filter((id): id is string => Boolean(id))
      );
      const addedIds = addedNames
        .map((name) => findPlantIdByName(name, availablePlants))
        .filter((id): id is string => Boolean(id));
      setSelectedPlants((prev) => {
        const next = prev.filter((id) => !removedIds.has(id));
        for (const id of addedIds) {
          if (!next.includes(id)) next.push(id);
        }
        return next;
      });
    }
  };

  // ── After-gen "Make it Happen" fork (PLA2-344) ──────────────────────────────
  // Purchase path: build a cart from the design state (selected plants × computed
  // quantities) and open the cart, which hands off to checkout. In D2C the cart is
  // tagged with the Plantista Shopify tenant so /api/checkout routes there
  // (PLA2-321/322); content still comes from the GC plant list.
  const addPlantsToCartAndOpen = (
    plants: Plant[],
    quantities: Record<string, number>
  ) => {
    // Preserve the user's in-cart edits: if the cart already holds items for
    // this design, just reopen it instead of reseeding from the computed
    // defaults (cart.addItems replaces the whole cart). Reseed only when the
    // cart is empty or belongs to a different design.
    const currentProjectId = projectId ?? undefined;
    if (cart.items.length > 0 && cart.metadata.projectId === currentProjectId) {
      setIsCartOpen(true);
      return;
    }

    const items: CartItem[] = plants
      // Drop synthetic care-item "supplies" (category "supply") — they have no
      // Shopify variant mapping yet and would fail SKU resolution at checkout.
      // Care add-ons in cart are PLA2-325.
      .filter((p) => p.category !== "supply")
      // Exclude plants the counter explicitly placed at 0 ("not used"). A missing
      // count (undefined) still falls back to 1 below.
      .filter((p) => quantities[p.id] !== 0)
      .map((p) => ({
        plant: p,
        quantity: effectivePlantQuantity(quantities[p.id]),
        unitPrice: p.price ?? null,
        genusAliasId: p.id,
        externalVariantId: p.shopifyVariantId ?? null,
      }));
    if (items.length === 0) {
      showError("Add at least one plant before checking out.");
      return;
    }
    cart.setMetadata({
      retailerId: isD2C ? D2C_STOREFRONT_TENANT_ID : retailerId,
      projectId: projectId ?? undefined,
      customerEmail: customerEmail ?? undefined,
      // PLA2-546: attribute the purchase to the design's recipe/style.
      recipeId: selectedRecipe?.id || selectedStyle || undefined,
      // PLA2-973: clear container attribution left by an earlier cart.
      flow: undefined,
      planterId: undefined,
    });
    cart.addItems(items);
    setIsCartOpen(true);
  };

  const handleAddToCart = (
    plants: Plant[],
    quantities: Record<string, number>
  ) => addPlantsToCartAndOpen(plants, quantities);

  // Purchase from the current design state (used by the prominent "Make it
  // Happen" fork and the stock-warning diversion).
  const goToPurchase = () =>
    addPlantsToCartAndOpen(
      availablePlants.filter((p) => selectedPlants.includes(p.id)),
      plantCounts
    );

  // "Send to Email" path: gate behind the stock-warning popup instead of sending
  // immediately, so the user can divert to Purchase or knowingly Send Anyway.
  const openEmailStockWarning = async () => {
    setIsStockWarningOpen(true);
  };

  const proceedToPurchaseFromWarning = () => {
    setIsStockWarningOpen(false);
    goToPurchase();
  };

  const sendDesignEmailAnyway = async () => {
    setIsSendingDesignEmail(true);
    try {
      await handleResendEmail();
      setIsStockWarningOpen(false);
    } catch {
      // handleResendEmail already surfaces the error toast; keep the popup open.
    } finally {
      setIsSendingDesignEmail(false);
    }
  };

  // Mint a signed 90-day capability link for this design and copy it to the
  // clipboard so it can be reopened on any device with no login (PLA2-314).
  const handleCopyShareLink = async () => {
    if (!projectId) {
      showError("Save your design first, then you can share it.");
      return;
    }
    setCopyingLink(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/resume-link`);
      if (!res.ok) throw new Error("Failed to create share link");
      const { url } = await res.json();
      await navigator.clipboard.writeText(url);
      showSuccess(
        "Resume link copied! It works on any device for 90 days.",
        "Link Copied"
      );
    } catch (error) {
      console.error("[Design Page] Failed to create share link:", error);
      showError(
        "Couldn't create the share link. Please try again.",
        "Share Failed"
      );
    } finally {
      setCopyingLink(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "upload":
        if (
          hasContainerAddon &&
          sessionRestored &&
          !gardenChosen &&
          !uploadedImageUrl &&
          !backendImageUrl &&
          !isUploading
        ) {
          return (
            <GardenTypeChooser
              onChoose={(type) => {
                capture(ANALYTICS_EVENTS.GARDEN_TYPE_SELECTED, {
                  garden_type: type,
                  account_type: isD2C ? "d2c" : "gc",
                  entry: "chooser",
                });
                // Browser Back from the container flow lands on the chooser
                // again, so only an outdoor choice is remembered.
                if (type === "outdoor") {
                  rememberGardenChosen(true);
                  return;
                }
                const q = new URLSearchParams();
                if (isD2C) q.set("d2c", "1");
                else q.set("retailerId", retailerId);
                // Counted here; the container page must not count it again.
                q.set("from", "chooser");
                router.push(`/design/container?${q.toString()}`);
              }}
            />
          );
        }
        return (
          <div className="space-y-3">
            {hasContainerAddon && (
              <div className="mb-3">
                <GardenTypeToggle
                  value="outdoor"
                  onChange={() => {
                    capture(ANALYTICS_EVENTS.GARDEN_TYPE_SELECTED, {
                      garden_type: "container",
                      account_type: isD2C ? "d2c" : "gc",
                      entry: "toggle",
                    });
                    const q = new URLSearchParams();
                    if (isD2C) q.set("d2c", "1");
                    else q.set("retailerId", retailerId);
                    // PLA2-973: tells the container page this entry was
                    // already counted, so it doesn't fire a second one.
                    q.set("from", "toggle");
                    router.push(`/design/container?${q.toString()}`);
                  }}
                />
              </div>
            )}
            <PhotoUploader
              onUpload={handleFileUpload}
              isUploading={isUploading}
              error={uploadError}
              disabled={serviceUnavailable}
            />
          </div>
        );

      case "area":
        return uploadedImageUrl ? (
          <>
            {/* Automatic photo check (PLA2-822) */}
            {showPhotoCheckWarning && photoCheck && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-3 rounded-xl border border-amber-400 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/20 dark:text-amber-100"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-bold">
                    Your yard only fills about{" "}
                    {Math.max(5, Math.round(photoCheck.yardFraction * 20) * 5)}%
                    of this photo.
                  </p>
                  <p>
                    The designer works best when your planting area fills at
                    least half the frame. Right now most of the photo is sky,
                    house or street.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    {uploadedFile && (
                      <button
                        type="button"
                        onClick={() => setIsCroppingUploaded(true)}
                        disabled={isUploading || serviceUnavailable}
                        className="inline-flex items-center gap-1.5 rounded-lg border-2 border-plantista-950 bg-plantista-400 px-3 py-1.5 text-sm font-bold text-plantista-950 shadow-[2px_2px_0px_0px] shadow-plantista-950 transition-all hover:bg-plantista-500 active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60 dark:shadow-plantista-50"
                      >
                        <Crop className="h-4 w-4" />
                        Crop to my yard
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleReplacePhotoClick}
                      disabled={isUploading || serviceUnavailable}
                      className="inline-flex items-center gap-1.5 rounded-lg border-2 border-plantista-950 bg-white px-3 py-1.5 text-sm font-bold text-plantista-950 transition-colors hover:bg-plantista-50 disabled:opacity-60 dark:border-plantista-50 dark:bg-plantista-900 dark:text-plantista-50 dark:hover:bg-plantista-800"
                    >
                      <ImagePlus className="h-4 w-4" />
                      Retake photo
                    </button>
                    <button
                      type="button"
                      onClick={() => setPhotoCheckDismissed(true)}
                      className="px-2 py-1.5 text-sm font-semibold underline underline-offset-2"
                    >
                      Continue anyway
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="relative">
              <AreaSelector
                imageUrl={uploadedImageUrl}
                onMaskGenerated={handleAreaSelected}
                onPolygonDataChange={setPolygonData}
                initialMask={selectedArea}
                polygonOverride={polygonOverride}
              />

              {/* Inline "replace photo" — a quiet glass pill on the canvas that
                  swaps the image in place without leaving the mask step. */}
              <button
                type="button"
                onClick={handleReplacePhotoClick}
                disabled={isUploading || serviceUnavailable}
                aria-label="Replace photo"
                className="group absolute top-3 right-3 z-20 flex items-center gap-2 rounded-full border border-white/50 dark:border-white/10 bg-white/70 dark:bg-plantista-900/60 px-3.5 py-2 text-sm font-semibold text-plantista-800 dark:text-plantista-100 shadow-lg backdrop-blur-md transition-all hover:bg-white/95 dark:hover:bg-plantista-900/85 hover:shadow-xl active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ImagePlus className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Replace photo</span>
              </button>

              {/* Brief in-place loading state while the new photo uploads. */}
              {isUploading && (
                <div className="absolute inset-0 z-30 flex items-center justify-center rounded-2xl bg-white/55 dark:bg-plantista-950/55 backdrop-blur-sm">
                  <div className="flex items-center gap-2 rounded-full bg-white/90 dark:bg-plantista-900/90 px-4 py-2 text-sm font-semibold text-plantista-800 dark:text-plantista-100 shadow-md">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Updating photo…
                  </div>
                </div>
              )}

              <input
                ref={replacePhotoInputRef}
                type="file"
                accept="image/*"
                onChange={handleReplacePhotoSelected}
                className="hidden"
              />
            </div>

            {isCroppingUploaded && uploadedFile && (
              <PhotoCropper
                file={uploadedFile}
                suggestedRect={
                  photoCheck?.url === backendImageUrl
                    ? photoCheck?.cropBox
                    : null
                }
                onCancel={() => setIsCroppingUploaded(false)}
                onCropped={(cropped) => {
                  setIsCroppingUploaded(false);
                  // Same pipeline as "Replace photo": re-uploads and clears
                  // the (too small) outline so the user redraws on the crop.
                  void handleFileUpload(cropped);
                }}
              />
            )}

            {/* Outline-coverage check (PLA2-822) */}
            {(isMaskTooSmall || isMaskSmall) && (
              <div
                role={isMaskTooSmall ? "alert" : "status"}
                className={`mt-4 flex items-start gap-3 rounded-xl border p-4 text-sm ${
                  isMaskTooSmall
                    ? "border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/20 dark:text-amber-100"
                    : "border-plantista-200 bg-plantista-50 text-plantista-800 dark:border-plantista-800 dark:bg-plantista-900/60 dark:text-plantista-200"
                }`}
              >
                {isMaskTooSmall ? (
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                ) : (
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-plantista-500 dark:text-plantista-400" />
                )}
                <div className="space-y-1">
                  <p className="font-bold">
                    {isMaskTooSmall
                      ? `Your outline covers only ${Math.max(1, Math.round((maskCoverage ?? 0) * 100))}% of the photo.`
                      : "Small area: expect fewer, smaller plants."}
                  </p>
                  {isMaskTooSmall ? (
                    <>
                      <p>
                        The designer needs a bigger area to place plants. Draw
                        beds at their full depth, not a thin line along the
                        edge. Around a tree, outline the whole bed, not just the
                        trunk.
                      </p>
                      <p>
                        Yard small in the frame?{" "}
                        {uploadedFile && (
                          <>
                            <button
                              type="button"
                              onClick={() => setIsCroppingUploaded(true)}
                              disabled={isUploading || serviceUnavailable}
                              className="font-semibold underline underline-offset-2"
                            >
                              Crop this photo to your yard
                            </button>{" "}
                            or{" "}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={handleReplacePhotoClick}
                          disabled={isUploading || serviceUnavailable}
                          className="font-semibold underline underline-offset-2"
                        >
                          {uploadedFile ? "use" : "Use"} a closer photo
                        </button>
                        , then redraw your outline.
                      </p>
                      {polygonData && (
                        <button
                          type="button"
                          onClick={handleFixOutline}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border-2 border-plantista-950 bg-plantista-400 px-3 py-1.5 text-sm font-bold text-plantista-950 shadow-[2px_2px_0px_0px] shadow-plantista-950 transition-all hover:bg-plantista-500 active:translate-x-[1px] active:translate-y-[1px] active:shadow-none dark:shadow-plantista-50"
                        >
                          <Wand2 className="h-4 w-4" />
                          Fix it for me
                        </button>
                      )}
                    </>
                  ) : (
                    <p>
                      Drawing the bed at its full depth gives the designer more
                      room to work with.
                    </p>
                  )}

                  {/* What are you outlining? → per-area drawing tip */}
                  <div className="pt-2">
                    <p className="text-xs font-semibold opacity-80">
                      What are you outlining?
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {OUTLINE_TIPS.map(({ kind, label }) => (
                        <button
                          key={kind}
                          type="button"
                          aria-pressed={outlineKind === kind}
                          onClick={() =>
                            setOutlineKind((k) => (k === kind ? null : kind))
                          }
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                            outlineKind === kind
                              ? "border-plantista-950 bg-plantista-400 text-plantista-950 dark:border-plantista-50"
                              : "border-current/30 bg-white/60 hover:bg-white dark:bg-white/5 dark:hover:bg-white/10"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {outlineKind && (
                      <p className="mt-2 text-sm">
                        {OUTLINE_TIPS.find((t) => t.kind === outlineKind)?.tip}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={handleContinueFromArea}
                disabled={!selectedArea || isMaskTooSmall}
                className="px-8 py-3 text-plantista-950 font-bold rounded-lg transition-all disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60 bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px]"
              >
                Continue to Location
              </button>
            </div>
          </>
        ) : null;

      case "preferences":
        return (
          <div className="space-y-4">
            <ZipcodeEntry
              onZipcodeSubmit={handleZipcodeSubmit}
              primaryColor={branding.primaryColor}
              initialZipcode={
                userZipcode || searchParams.get("zip") || undefined
              }
              isRetailerFlow={isRetailerLocked}
            />
            {/* "Why we ask" value note (PLA2-318) */}
            <p className="text-center text-xs text-plantista-500 dark:text-plantista-400 max-w-md mx-auto">
              Why we ask: your ZIP tailors plant picks to your climate (USDA
              hardiness zone)
              {!isRetailerLocked &&
                " — and connects you with a trusted local partner if one serves your area"}
              .
            </p>
          </div>
        );

      // PLA2-318: a genuine choice — continue to the local partner, or stay with
      // Plantista — so this screen waits for the user. It used to auto-redirect
      // on a 4s timer, which took the decision away before the copy could be read.
      case "handoff":
        return handoffPartner ? (
          <div className="max-w-lg mx-auto text-center bg-white dark:bg-plantista-900 border-2 border-plantista-950 dark:border-plantista-50 rounded-2xl p-8 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-plantista-100 dark:bg-plantista-800">
              <Sparkles className="h-6 w-6 text-plantista-600 dark:text-plantista-300" />
            </div>
            <h2 className="text-xl font-display font-bold text-plantista-950 dark:text-white mb-2">
              There&apos;s a local partner near you
            </h2>
            <p className="text-sm text-plantista-600 dark:text-plantista-400 mb-6">
              <strong className="text-plantista-800 dark:text-plantista-200">
                {handoffPartner.name}
              </strong>{" "}
              designs gardens for your area — so you get locally-stocked plants,
              pricing, and pickup. Continue with them, or stay with Plantista.
            </p>
            <button
              onClick={() => {
                // Hand off in place: same origin, so the photo, mask, ZIP and
                // zone all survive and the user lands on the next step rather
                // than restarting on the partner's landing page.
                const partner = handoffPartner;
                setHandoffPartner(null);
                void handleSelectRetailer(partner.id, userZipcode || "");
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 text-plantista-950 font-bold rounded-lg bg-plantista-400 hover:bg-plantista-500 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] transition-all"
            >
              Continue to {handoffPartner.name}
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                setHandoffPartner(null);
                void handleSelectGeneral(userZipcode || "");
              }}
              className="block mx-auto mt-4 text-xs text-plantista-500 dark:text-plantista-400 hover:underline"
            >
              Continue with Plantista instead
            </button>
          </div>
        ) : null;

      // PLA2-688: the retailer can't ship to this state. The designer can be
      // embedded on the retailer's storefront, so both links break out of the
      // iframe (target="_top").
      case "shipRestricted":
        return shipRestriction ? (
          <div className="max-w-lg mx-auto text-center bg-white dark:bg-plantista-900 border-2 border-plantista-950 dark:border-plantista-50 rounded-2xl p-8 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-plantista-100 dark:bg-plantista-800">
              <Truck className="h-6 w-6 text-plantista-600 dark:text-plantista-300" />
            </div>
            <h2 className="text-xl font-display font-bold text-plantista-950 dark:text-white mb-2">
              We can&apos;t ship to {shipRestriction.stateName} yet
            </h2>
            <p className="text-sm text-plantista-600 dark:text-plantista-400 mb-6">
              Sorry! State plant-shipping rules don&apos;t allow us to send our
              perennials to {shipRestriction.stateName}, so the Garden Designer
              can&apos;t build a plan you can order from us. You can still
              design a garden made for your region with Plantista&apos;s free AI
              Plantscape Designer.
            </p>
            <a
              href={plantistaRedirectUrl(shipRestriction, userZipcode || "")}
              target="_top"
              onClick={() =>
                capture(ANALYTICS_EVENTS.SHIP_RESTRICTED_PLANTISTA_CLICKED, {
                  state: shipRestriction.state,
                })
              }
              className="inline-flex items-center justify-center gap-2 px-8 py-3 text-plantista-950 font-bold rounded-lg bg-plantista-400 hover:bg-plantista-500 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] transition-all"
            >
              Design my garden on Plantista
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={shipRestriction.config.storefrontUrl}
              target="_top"
              onClick={() =>
                capture(ANALYTICS_EVENTS.SHIP_RESTRICTED_BACK_CLICKED, {
                  state: shipRestriction.state,
                })
              }
              className="block mx-auto mt-4 text-xs text-plantista-500 dark:text-plantista-400 hover:underline"
            >
              Back to {shipRestriction.config.retailerName}
            </a>
            <button
              onClick={() => {
                setShipRestriction(null);
                setStep("preferences");
              }}
              className="block mx-auto mt-2 text-xs text-plantista-500 dark:text-plantista-400 hover:underline"
            >
              Use a different ZIP code
            </button>
          </div>
        ) : null;

      case "retailer-select":
        return (
          <RetailerSelector
            retailers={nearbyRetailers}
            onSelectRetailer={(id) => handleSelectRetailer(id, userZipcode!)}
            onSelectGeneral={() => handleSelectGeneral(userZipcode!)}
            primaryColor={branding.primaryColor}
            zipcode={userZipcode || ""}
          />
        );

      case "style":
        return (
          <>
            <StylePicker
              styles={recipes}
              selectedStyle={selectedStyle || undefined}
              onSelect={handleStyleSelected}
              loading={loadingRecipes}
              primaryColor={branding.primaryColor}
              userZone={userZone}
              availability={recipeAvailability}
              plantAvailability={plantAvailability}
            />
          </>
        );

      case "plants":
        return (
          <>
            {/* Sub-step indicator */}
            <div className="flex items-center gap-2 text-sm px-4 md:px-0 mb-2">
              <button
                onClick={() => setPlantSubStep("plants")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all font-medium ${
                  plantSubStep === "plants"
                    ? "text-plantista-950 shadow-md"
                    : selectedPlants.length > 0
                      ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 hover:bg-plantista-200 dark:hover:bg-plantista-700"
                      : "bg-gray-100 dark:bg-plantista-900 text-gray-500 dark:text-plantista-500"
                }`}
                style={
                  plantSubStep === "plants"
                    ? { backgroundColor: branding.primaryColor }
                    : undefined
                }
              >
                <Leaf className="w-3.5 h-3.5" />
                <span>Select Plants</span>
                {selectedPlants.length > 0 && plantSubStep !== "plants" && (
                  <Check className="w-3.5 h-3.5 text-plantista-500" />
                )}
              </button>
              <ArrowRight className="w-4 h-4 text-gray-400 dark:text-plantista-600" />
              <button
                onClick={() => {
                  if (selectedPlants.length > 0) setPlantSubStep("gen-options");
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all font-medium ${
                  plantSubStep === "gen-options"
                    ? "text-plantista-950 shadow-md"
                    : selectedPlants.length > 0
                      ? "bg-gray-100 dark:bg-plantista-900 text-gray-600 dark:text-plantista-400 hover:bg-plantista-100 dark:hover:bg-plantista-800"
                      : "bg-gray-100 dark:bg-plantista-900 text-gray-400 dark:text-plantista-600 cursor-not-allowed"
                }`}
                style={
                  plantSubStep === "gen-options"
                    ? { backgroundColor: branding.primaryColor }
                    : undefined
                }
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Generation Options</span>
              </button>
            </div>

            {plantSubStep === "plants" && (
              <>
                <PlantList
                  plants={availablePlants}
                  selectedPlants={selectedPlants}
                  onTogglePlant={handleTogglePlant}
                  onViewPlantDetails={(plant) =>
                    setSelectedPlantForModal(plant)
                  }
                  recipeInfo={selectedRecipe || undefined}
                  loading={loadingPlants}
                  soldOutPlantIds={soldOutPlantIdsList}
                  substitutedFor={substitutedFor}
                />

                {/* Fixed so it's visible wherever the click came from — the
                    grid or the plant details modal (z-50) — PLA2-762. */}
                <div
                  role="status"
                  aria-live="polite"
                  className="fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4 pointer-events-none"
                >
                  {plantLimitNotice && (
                    <div className="pointer-events-auto max-w-md flex items-start gap-3 rounded-lg border-2 border-plantista-950 dark:border-plantista-100 bg-white dark:bg-plantista-900 px-4 py-3 text-sm text-plantista-950 dark:text-plantista-50 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50">
                      <p>
                        You can pick up to {MAX_SELECTED_PLANTS} plants. Our AI
                        works best with {MAX_SELECTED_PLANTS} or fewer, so
                        uncheck one to swap it in.
                      </p>
                      <button
                        onClick={() => setPlantLimitNotice(false)}
                        className="shrink-0 font-bold text-plantista-600 dark:text-plantista-300 hover:text-plantista-950 dark:hover:text-white"
                        aria-label="Dismiss"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                <PlantDetailsModal
                  plant={selectedPlantForModal}
                  isOpen={!!selectedPlantForModal}
                  onClose={() => setSelectedPlantForModal(null)}
                  onToggleSelection={handleTogglePlant}
                  isSelected={
                    selectedPlantForModal
                      ? selectedPlants.includes(selectedPlantForModal.id)
                      : false
                  }
                  primaryColor={branding.primaryColor}
                  soldOut={
                    selectedPlantForModal
                      ? soldOutPlantIds.has(selectedPlantForModal.id)
                      : false
                  }
                />

                {!loadingPlants && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-plantista-600 dark:text-plantista-400 font-medium">
                      {selectedPlants.length} / {MAX_SELECTED_PLANTS} plants
                      selected
                    </p>
                    {selectedPlants.length > 0 && (
                      <button
                        onClick={() => setPlantSubStep("gen-options")}
                        className="flex items-center gap-2 px-8 py-3 text-plantista-950 font-bold rounded-lg transition-all bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px]"
                      >
                        Continue
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {plantSubStep === "gen-options" && (
              <div className="space-y-4 px-4 md:px-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
                      Generation Options
                    </h2>
                    <p className="text-sm text-plantista-600 dark:text-plantista-300 mt-1">
                      Customize how your design is generated. These options add
                      extra detail but increase generation time.
                    </p>
                  </div>
                  <button
                    onClick={() => setPlantSubStep("plants")}
                    className="flex items-center gap-1 text-sm text-plantista-600 dark:text-plantista-400 hover:text-plantista-800 dark:hover:text-plantista-200 transition-colors shrink-0"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Edit plants</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* All Seasons Card */}
                  <button
                    onClick={() => setGenerateAllSeasons(!generateAllSeasons)}
                    className={`relative flex items-start gap-4 p-5 rounded-xl border-2 transition-all text-left group ${
                      generateAllSeasons
                        ? "border-plantista-500 dark:border-plantista-400 bg-plantista-50 dark:bg-plantista-400/10 ring-2 ring-plantista-500/50 dark:ring-plantista-400/70 shadow-lg shadow-plantista-500/20 dark:shadow-plantista-400/30"
                        : "border-gray-200 dark:border-plantista-800 bg-white dark:bg-plantista-900 hover:border-plantista-300 dark:hover:border-plantista-600 hover:shadow-md"
                    }`}
                  >
                    {generateAllSeasons && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center bg-plantista-600 dark:bg-plantista-400 shadow-sm">
                        <Check className="w-3 h-3 text-white dark:text-plantista-950" />
                      </div>
                    )}
                    <div
                      className={`p-3 rounded-lg shrink-0 ${
                        generateAllSeasons
                          ? "bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow-md"
                          : "bg-gray-100 dark:bg-plantista-800/50 text-gray-500 dark:text-plantista-400 group-hover:bg-plantista-100 dark:group-hover:bg-plantista-800 group-hover:text-plantista-600"
                      }`}
                    >
                      <Calendar className="w-8 h-8" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-plantista-950 dark:text-white">
                          Generate All Seasons
                        </h3>
                      </div>
                      <p className="text-sm text-plantista-600 dark:text-plantista-400 mt-1 leading-relaxed">
                        Create designs for Spring, Summer, Autumn, and Winter —
                        see how your garden transforms throughout the year.
                      </p>
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-plantista-500 dark:text-plantista-500">
                        <Clock className="w-3 h-3" />
                        <span>Adds ~1–2 minutes to generation time</span>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Expectation setting (PLA2-822) */}
                <div className="flex items-start gap-2.5 rounded-xl border border-plantista-200 dark:border-plantista-800 bg-plantista-50/60 dark:bg-plantista-900/60 p-4 text-sm text-plantista-700 dark:text-plantista-300">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-plantista-500 dark:text-plantista-400" />
                  <p>
                    Your design will fill the outlined area with your selected
                    plants at about 3-year size. Small or narrow areas get
                    fewer, smaller plants. Everything outside your outline stays
                    as it is.{" "}
                    <span className="font-semibold">
                      Your design is a visual guide. Your plant list is exact.
                    </span>
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleGenerateClick}
                    disabled={serviceUnavailable}
                    title={
                      serviceUnavailable
                        ? "The designer is temporarily unavailable"
                        : undefined
                    }
                    className="flex items-center gap-2 px-8 py-3 text-plantista-950 font-bold rounded-lg transition-all bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:translate-x-0 disabled:translate-y-0 disabled:hover:bg-plantista-400"
                  >
                    <Sparkles className="w-4 h-4" />
                    Generate{" "}
                    {generateAllSeasons ? "All Seasons" : "Summer Only"} (
                    {selectedPlants.length} plant
                    {selectedPlants.length !== 1 ? "s" : ""})
                  </button>
                </div>
              </div>
            )}
          </>
        );

      case "generate":
      case "result":
        if (isGenerating) {
          return (
            <GenerationLoader
              plants={availablePlants.filter((p) =>
                selectedPlants.includes(p.id)
              )}
              recipeName={selectedRecipe?.name}
              uploadedImageUrl={uploadedImageUrl || undefined}
              progress={jobId ? jobProgress : undefined}
              status={
                jobStatus === "queued" ||
                jobStatus === "processing" ||
                jobStatus === "rate_limited"
                  ? jobStatus
                  : undefined
              }
              queuePosition={queuePosition}
              estimatedWaitSeconds={estimatedWait}
              allSeasons={generateAllSeasons}
            />
          );
        }

        return generationResults ? (
          <div className="space-y-12">
            {/* Shareable resume link — reopen this design on any device, no login */}
            <div className="flex justify-end">
              <button
                onClick={handleCopyShareLink}
                disabled={copyingLink || !projectId}
                className="group flex items-center gap-2 px-4 py-2 text-sm font-bold text-plantista-700 dark:text-plantista-300 rounded-lg border border-plantista-200 dark:border-plantista-800 hover:bg-plantista-100 dark:hover:bg-plantista-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                title="Copy a link to resume this design on any device"
              >
                <Share2 className="h-4 w-4" />
                {copyingLink ? "Creating link…" : "Copy share link"}
              </button>
            </div>
            <ResultViewer
              results={generationResults}
              originalImageUrl={uploadedImageUrl!}
              initialEditHistories={editHistories}
              initialEditedResults={editedResults}
              onResultsUpdate={(season, newImageUrl) => {
                setGenerationResults((prev) =>
                  prev ? { ...prev, [season]: newImageUrl } : prev
                );
              }}
              onEditHistoriesChange={setEditHistories}
              onEditedResultsChange={setEditedResults}
              sunLevel={selectedRecipe?.sunLevel}
              styleName={selectedRecipe?.name}
              zone={userZone || undefined}
              projectId={projectId || undefined}
              countPlants
              generateDiagram={Boolean(generationResults?.diagramUrl)}
              candidatePlantIds={selectedPlants}
              priorCounts={plantCounts}
              onPlantChanges={handleEditPlantChanges}
            />

            <div id="shopping-list" />
            {loadingPlants ||
            (selectedStyle && recipes.length === 0) ||
            (selectedStyle &&
              availablePlants.length === 0 &&
              loadingRecipes) ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-b-2"
                  style={{ borderColor: branding.primaryColor }}
                />
                <span className="ml-3 text-sm text-plantista-600 dark:text-plantista-400">
                  Loading shopping list...
                </span>
              </div>
            ) : (
              <ShoppingList
                plants={availablePlants.filter((p) =>
                  selectedPlants.includes(p.id)
                )}
                quantities={plantCounts}
                // Sold-out plants dropped from the selection (PLA2-458).
                unavailablePlants={unavailablePlants}
                replacedFor={replacedFor}
                retailerBranding={{
                  companyName: branding.companyName,
                  logo: branding.logo,
                  primaryColor: branding.primaryColor,
                }}
                retailerId={retailerId}
                // Commerce tenant for live Shopify quotes (PLA2-412): D2C
                // browses the GC catalog but prices via the D2C Shopify tenant
                // — same swap as cart.setMetadata in addPlantsToCartAndOpen.
                commerceTenantId={isD2C ? D2C_STOREFRONT_TENANT_ID : retailerId}
                // After-gen fork (PLA2-344): D2C surfaces Purchase ("Reserve My
                // Plants") + routes "Send to Email" through the stock-warning
                // popup. Retailer/GC flows keep the direct email send unchanged.
                // Retailer flows also get Purchase, but ShoppingList only shows
                // it once live quotes confirm the retailer's own Shopify store
                // is selling (commerce_connection enabled).
                onAddToCart={handleAddToCart}
                onSendEmail={
                  isEcommerce ? openEmailStockWarning : handleResendEmail
                }
                // Ecommerce flows consolidate the Reserve/Email CTAs into the
                // "Make it Happen" box below the list (PLA2-344), so hide them here.
                showActions={!isEcommerce}
                // Recommended Supplies is flagged off for D2C (PLA2-764).
                showRecommendations={tenant.features.productRecommendations}
                // Number rows with the planting diagram's key badges.
                numbered={Boolean(generationResults?.diagramUrl)}
                sunLevel={selectedRecipe?.sunLevel}
                styleName={selectedRecipe?.name}
                zone={userZone || undefined}
                designImages={
                  generationResults
                    ? {
                        spring: generationResults.spring,
                        summer: generationResults.summer,
                        autumn: generationResults.autumn,
                        winter: generationResults.winter,
                      }
                    : undefined
                }
                coupons={(() => {
                  const FOSSIL_CREEK_ID =
                    "e955b2c5-d47d-4ae4-971f-36ed0deb87f2";
                  if (retailerId === FOSSIL_CREEK_ID) return [];
                  const rawCoupons = retailerData?.coupons;
                  if (!rawCoupons?.length) return [];
                  const ALLOWED_PURPOSES = ["PLANTING", "FERTILIZING"];
                  return rawCoupons
                    .filter(
                      (c: any) =>
                        !c.deleted && ALLOWED_PURPOSES.includes(c.purpose)
                    )
                    .map((c: any) => ({
                      code: c.code || null,
                      purpose: c.purpose,
                      imageUrl: c.image_url || c.imageUrl || null,
                      minPurchaseRequirement:
                        c.min_purchase_requirement ?? c.minPurchaseRequirement,
                      relativeExpirationDays:
                        c.relative_expiration_days ?? c.relativeExpirationDays,
                    }));
                })()}
                designCoupon={(() => {
                  const rawCoupons = retailerData?.coupons;
                  if (!rawCoupons?.length) return null;
                  const dc = rawCoupons.find(
                    (c: any) =>
                      !c.deleted &&
                      String(c.purpose || "").toUpperCase() === "DESIGN"
                  );
                  if (!dc) return null;
                  return {
                    couponHeading:
                      dc.coupon_heading || dc.couponHeading || null,
                    value: dc.value ?? null,
                    type: dc.type ?? null,
                    name: dc.name ?? null,
                    code: dc.code || null,
                    imageUrl:
                      dc.designed_image_url ||
                      dc.designedImageUrl ||
                      dc.image_url ||
                      dc.imageUrl ||
                      null,
                    minPurchaseRequirement:
                      dc.min_purchase_requirement ??
                      dc.minPurchaseRequirement ??
                      null,
                    relativeExpirationDays:
                      dc.relative_expiration_days ??
                      dc.relativeExpirationDays ??
                      null,
                  };
                })()}
              />
            )}

            {/* Planting guide card under the list, as on the container
                result. An edit that regenerated the diagram wins. */}
            {(editedResults.diagramUrl || generationResults.diagramUrl) && (
              <PlantingGuideCard
                diagramUrl={
                  editedResults.diagramUrl || generationResults.diagramUrl!
                }
                gardenUrl={editedResults.summer || generationResults.summer}
              />
            )}

            {/* "Make it Happen" fork (PLA2-344), shown below the shopping
                list. Purchase locks plants in via checkout; Send to Email
                gates through the stock-warning popup. Ecommerce only (D2C +
                selling GCs) — its CTAs replace the in-list Reserve/Email buttons. */}
            {isEcommerce && generationResults && (
              <MakeItHappen
                body={
                  retailerHasCoupon
                    ? "Get your plants delivered, or email the design and your coupon to yourself."
                    : "Get your plants delivered, or email the design to yourself."
                }
                purchaseLabel="Purchase my garden"
                onPurchase={goToPurchase}
                onEmail={openEmailStockWarning}
              />
            )}

            {/* Horticultural / spacing / suitability / 811 / HOA disclaimer
                (PLA2-371) — moved here from ResultViewer so it sits under the
                "Make it Happen" CTA at the bottom of the result page. */}
            <HorticulturalDisclaimer className="mx-auto max-w-2xl" />
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg font-medium">
              No design results available. Please try generating again.
            </p>
            <button
              onClick={() => setStep("plants")}
              className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-full font-bold shadow-md hover:bg-emerald-700"
            >
              Back to Plants
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // PLA2-947: while a blocking dependency is down, the whole step area is
  // replaced by the outage page (waitlist + "visit the store"). One exception:
  // a visitor already looking at a finished design keeps it, with the PLA2-945
  // banner, so a mid-session outage never takes their result off the screen.
  // Checked before the retailer spinner, whose fetch may be the thing hanging.
  const showOutagePage =
    serviceUnavailable && !(step === "result" && generatedImageUrl);
  if (showOutagePage) {
    // A partner store is "resolved" once it is locked by ?retailerId= or chosen
    // after the ZIP step. The D2C / default-storefront flow has no store to
    // send people to, so the store block is hidden and the signup is filed
    // under the Plantista garden-center retailer (a real Plantista retailer
    // row; the D2C storefront id is a Shopify commerce tenant, not one).
    const storeResolved = !isD2C && retailerSelected;
    const outageRetailerId = storeResolved
      ? retailerId
      : PLANTISTA_GC_RETAILER_ID;
    const outageStore = storeResolved
      ? buildOutageStore(retailerData, cachedBranding?.companyName)
      : null;
    return (
      <TenantProvider tenant={tenant}>
        <div className="min-h-screen flex flex-col bg-plantista-50 dark:bg-plantista-950 text-plantista-950 dark:text-plantista-50 transition-colors duration-300">
          <Header minimal={!retailerSelected} />
          <main className="flex-1 pt-32 pb-12">
            <div className="container mx-auto px-4 max-w-6xl">
              <DesignerOutagePage
                retailerId={outageRetailerId}
                store={outageStore}
                marketingBrand={outageStore?.name || "Plantista"}
                accountType={isD2C ? "d2c" : "gc"}
                onRetry={recheckServices}
                checking={serviceChecking}
              />
            </div>
          </main>
          <Footer />
        </div>
      </TenantProvider>
    );
  }

  // Show loading state while fetching retailer data (after retailer is selected)
  if (loadingRetailer && retailerSelected) {
    return (
      <TenantProvider tenant={tenant}>
        <Header minimal={false} />
        <main className="flex-1 py-12 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
              style={{ borderColor: branding.primaryColor }}
            ></div>
            <p className="text-gray-600">Loading {branding.companyName}...</p>
          </div>
        </main>
        <Footer />
      </TenantProvider>
    );
  }

  return (
    <TenantProvider tenant={tenant}>
      {/* Theme-aware container */}
      <div className="min-h-screen flex flex-col bg-plantista-50 dark:bg-plantista-950 text-plantista-950 dark:text-plantista-50 transition-colors duration-300">
        <Header minimal={!retailerSelected} />
        <main className="flex-1 pt-32 pb-12">
          <div className="container mx-auto px-4 max-w-6xl">
            {serviceUnavailable && (
              <ServiceUnavailableBanner
                onRetry={recheckServices}
                checking={serviceChecking}
                className="mb-8 max-w-4xl mx-auto"
              />
            )}
            {/* Progress Overview */}
            <nav
              aria-label="Design progress"
              className="mb-12 max-w-4xl mx-auto px-4"
            >
              <div className="relative">
                {/* Background connecting line — inset to start/end at first/last step centers */}
                <div
                  aria-hidden="true"
                  className="absolute top-5 h-[2px] bg-gray-200 dark:bg-plantista-800 -translate-y-1/2"
                  style={{
                    left: `${100 / (progressSteps.length * 2)}%`,
                    right: `${100 / (progressSteps.length * 2)}%`,
                  }}
                />

                {/* Progress line — inset to match */}
                <div
                  aria-hidden="true"
                  className="absolute top-5 h-[2px] bg-plantista-500 dark:bg-plantista-400 -translate-y-1/2 transition-all duration-500 ease-in-out"
                  style={{
                    left: `${100 / (progressSteps.length * 2)}%`,
                    width: `${
                      (Math.max(
                        0,
                        progressSteps.findIndex(
                          (ps) => ps.key === getProgressStep(step)
                        )
                      ) /
                        (progressSteps.length - 1)) *
                      (100 - 100 / progressSteps.length)
                    }%`,
                  }}
                />

                <div className="relative flex justify-between">
                  {progressSteps.map((s, index) => {
                    // Map internal steps to display steps for progress
                    const currentProgressStep = getProgressStep(step);
                    const isCurrent = s.key === currentProgressStep;
                    const isCompleted = completedSteps.has(s.key);
                    const isActive = isCompleted || isCurrent;
                    // All steps at or before current index are clickable + any completed step is clickable
                    const currentProgressIndex = progressSteps.findIndex(
                      (ps) => ps.key === currentProgressStep
                    );
                    const isClickable =
                      index <= currentProgressIndex || isCompleted;

                    return (
                      <div
                        key={s.key}
                        className="flex flex-col items-center flex-1"
                      >
                        <button
                          onClick={() => handleStepClick(s.key)}
                          disabled={!isClickable}
                          aria-current={isCurrent ? "step" : undefined}
                          aria-label={`Step ${index + 1} of ${progressSteps.length}: ${s.label}${
                            isCompleted
                              ? " (completed)"
                              : isCurrent
                                ? " (current step)"
                                : ""
                          }`}
                          className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 dark:focus-visible:ring-plantista-400 focus-visible:ring-offset-2 focus-visible:ring-offset-plantista-50 dark:focus-visible:ring-offset-plantista-950
                            ${
                              isCurrent
                                ? "border-plantista-500 dark:border-plantista-400 bg-white dark:bg-plantista-900 text-plantista-600 dark:text-plantista-400 ring-4 ring-plantista-50 dark:ring-plantista-900"
                                : isCompleted
                                  ? "border-plantista-500 dark:border-plantista-400 bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950"
                                  : "border-gray-200 dark:border-plantista-800 bg-white dark:bg-plantista-950 text-gray-400 dark:text-plantista-600"
                            }
                            ${isClickable ? "cursor-pointer hover:scale-105" : "cursor-not-allowed"}
                          `}
                          title={s.label}
                        >
                          {index + 1}
                        </button>
                        <div className="mt-3 text-center">
                          <div
                            className={`text-xs font-bold transition-colors duration-300 ${isActive ? "text-plantista-900 dark:text-white" : "text-gray-400 dark:text-plantista-600"}`}
                          >
                            {s.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reset button — visible once past the first step */}
              {step !== "upload" && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleStartOver}
                    className="group flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 dark:text-plantista-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-900 transition-all duration-200"
                  >
                    <RefreshCw
                      className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-180"
                      aria-hidden="true"
                    />
                    Start Over
                  </button>
                </div>
              )}
            </nav>

            {/* Per-step value nugget — D2C guided flow only (PLA2-340) */}
            {isD2C && <StepValueNugget step={getProgressStep(step)} />}

            {/* Current Step Content */}
            <div className="mt-8 bg-plantista-50 dark:bg-plantista-950 text-plantista-950 dark:text-plantista-50">
              {renderStep()}
            </div>
          </div>
        </main>
        <Footer />

        {/* Email Modal - shown before generation */}
        <EmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={handleEmailSubmit}
          isSending={false}
        />

        {/* Cart Modal - slide-over */}
        <CartModal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

        {/* Out-of-stock alternatives ("Looking for alternatives…") */}
        <PlantAlternativesDialog
          isOpen={alternativesDialog !== null}
          status={alternativesDialog?.status ?? "loading"}
          result={alternativesDialog?.result ?? null}
          recipeName={alternativesDialog?.recipeName ?? ""}
          storeName={retailerData?.business_name || undefined}
          trigger={alternativesDialog?.trigger ?? "select"}
          onConfirm={handleAlternativesConfirm}
          onBack={handleAlternativesBack}
          onRetry={() => {
            if (!alternativesDialog) return;
            const { recipeId, recipeName, trigger, plantIds } =
              alternativesDialog;
            openAlternatives(recipeId, recipeName, trigger, plantIds);
          }}
        />

        {/* After-gen "Send to Email" stock-warning popup (PLA2-344) */}
        <StockWarningModal
          isOpen={isStockWarningOpen}
          onClose={() => setIsStockWarningOpen(false)}
          onProceedToPurchase={proceedToPurchaseFromWarning}
          onSendAnyway={sendDesignEmailAnyway}
          isSending={isSendingDesignEmail}
        />

        {/* Alert Modal - custom styled alerts */}
        <AlertModal
          isOpen={isAlertOpen}
          onClose={closeAlert}
          onConfirm={alertConfig.onConfirm}
          title={alertConfig.title}
          message={alertConfig.message}
          type={alertConfig.type}
          confirmText={alertConfig.confirmText}
          cancelText={alertConfig.cancelText}
        />
      </div>
    </TenantProvider>
  );
}

export default function DesignPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-plantista-50 dark:bg-plantista-950">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-plantista-500 mx-auto mb-4"></div>
            <p className="text-plantista-700 dark:text-plantista-300">
              Loading...
            </p>
          </div>
        </div>
      }
    >
      <DesignPageContent />
    </Suspense>
  );
}
